<template>
  <div class="content-panel step-three-panel">
    <div class="panel-header">
      <div class="panel-icon">
        <v-icon size="32" color="#1e3a8a">mdi-cog</v-icon>
      </div>
      <div class="panel-title-section">
        <h2 class="panel-title">급여 상세 설정하기</h2>
      </div>
      <div class="panel-header-actions">
        <template v-if="isConfirmed">
          <button class="btn-header-action" @click="handleRelease" :disabled="isConfirming">
            <v-progress-circular
              v-if="isConfirming"
              indeterminate
              size="16"
              width="2"
              color="#f59e0b"
              class="mr-1"
            />
            <v-icon v-else size="18">mdi-lock-open-variant</v-icon>
            <span>확정 해제</span>
          </button>
        </template>
        <template v-else>
          <button 
            class="btn-header-action primary confirm-emphasis" 
            @click="handleConfirm" 
            :disabled="!stepCompleted || isConfirming || hasUnsavedChanges"
          >
            <v-progress-circular
              v-if="isConfirming"
              indeterminate
              size="16"
              width="2"
              color="#fff"
              class="mr-1"
            />
            <v-icon v-else size="18">mdi-lock-check</v-icon>
            <span>급여 설정 확정하기</span>
          </button>
        </template>
      </div>
    </div>

    <div class="panel-body">
      <!-- 확정 상태 알림 배너 -->
      <div v-if="isConfirmed" class="confirmed-notice-banner">
        <v-icon size="20" color="#f59e0b">mdi-lock</v-icon>
        <span class="notice-text">
          급여 설정이 <strong>확정</strong>되어 수정할 수 없습니다.
          수정하려면 상단의 <strong>확정 해제</strong> 버튼을 눌러주세요.
        </span>
        <button class="btn-release-inline" @click="handleRelease" :disabled="isConfirming">
          <v-icon size="16">mdi-lock-open-variant</v-icon>
          확정 해제
        </button>
      </div>

      <!-- 전체급여상세 컴포넌트 -->
      <WageWholeVersion
        v-if="showWholeVersion && memberInfoData.length > 0"
        v-model="showWholeVersion"
        :grouped-member-data="groupedMemberData"
        :payroll-details="payrollDetails"
        :settings-data="settingsData"
        :year-month="yearMonth"
        @close="showWholeVersion = false"
      />

      <!-- 사원 급여 정보 리스트 (새 컴포넌트) -->
      <WageEmployeeList
        v-if="memberInfoData.length > 0"
        :employees="groupedMemberData"
        :payroll-details="payrollDetails"
        :settings-data="settingsData"
        :services="individualServices"
        :show-whole-version="showWholeVersion"
        :has-unsaved-changes="hasUnsavedChanges"
        :is-saving="isSaving"
        :is-confirmed="isConfirmed"
        @open-detail="openEmployeeModal"
        @toggle-whole-version="showWholeVersion = !showWholeVersion"
        @open-settings="showSetModal = true"
        @save-all="saveSettings"
      />

      <!-- 사원 급여 상세 모달 (새 컴포넌트) -->
      <WageEmployeeModal
        v-model="showEmployeeModal"
        :employee="selectedEmployee"
        :employees="groupedMemberData"
        :group-index="selectedEmployeeIndex"
        :year-month="yearMonth"
        :sisul-code="store.state.user?.m_sisul_code"
        :payroll-detail="payrollDetails[selectedEmployeeIndex]"
        :settings="settingsData[selectedEmployeeIndex]"
        :services="individualServices[selectedEmployeeIndex] || []"
        :weekly-services="combinedWeeklyServices[selectedEmployeeIndex] || []"
        :severe-care-items="getSevereCareAdditionForGroup(selectedEmployeeIndex)"
        :severe-care-daily="severeCareAdditionDaily[selectedEmployeeIndex] || []"
        :holidays="holidaysData"
        :is-confirmed="isConfirmed"
        :center-profit="selectedEmployee ? calculateCenterProfit(selectedEmployee, selectedEmployeeIndex) : 0"
        :cumulative-retirement="selectedEmployee?.name ? cumulativeRetirement[selectedEmployee.name] || 0 : 0"
        :employee-info-items="selectedEmployee ? getEmployeeInfoItems(selectedEmployee, selectedEmployeeIndex) : []"
        :restriction-items="selectedEmployee ? getRestrictionItems(selectedEmployee, selectedEmployeeIndex) : []"
        :has-changes="hasUnsavedChanges"
        :is-saving="isSaving"
        @update:group-index="selectedEmployeeIndex = $event; selectedEmployee = groupedMemberData[$event]"
        @update-setting="handleUpdateSetting"
        @update-timepay="handleUpdateTimepay"
        @open-bonin-modal="openBoninPayModal"
        @open-employment-modal="openEmploymentModal"
        @open-suga-hours-modal="openSugaHoursModalFromModal"
        @reset-settings="resetSingleCaregiverSettings"
        @save-settings="saveSettings"
        @load-previous-settings="loadPreviousSettings"
        @start-date-updated="handleStartDateUpdated"
      />

      <!-- 기존 테이블 코드 제거됨 - WageEmployeeList로 대체 -->
      <div v-if="false" class="data-table-container">
        <div class="table-scroll-wrapper">
          <table class="data-table">
            <thead>
              <tr>
                <th>성명</th>
                <th class="time-pay-header">시급</th>
                <th>총수가</th>
                <th>월급여</th>
                <th>기관수익</th>
                <th class="input-header">국민연금</th>
                <th class="checkbox-header">건강보험<br/>적용</th>
                <th class="checkbox-header">고용보험<br/>적용</th>
                <th class="checkbox-header">퇴직금<br/>적립</th>
                <th class="checkbox-header">휴일수당<br/>지급</th>
                <th class="checkbox-header">연장수당<br/>지급</th>
                <th class="checkbox-header">식대<br/>지급</th>
                <th class="input-header">장기근속<br/>수당</th>
                <th class="input-header">본인부담금<br/>공제</th>
                <th class="input-header">기타공제</th>

              </tr>
            </thead>
            <tbody>
            <template v-for="(group, groupIndex) in groupedMemberData" :key="groupIndex">
              <!-- 메인 행 -->
              <tr v-if="settingsData[groupIndex]" class="main-row">
                <td class="merged-cell caregiver-info">{{ group.name || '-' }}</td>
                <td 
                  class="total-cell time-pay-cell clickable"
                  @click="toggleTimePay(groupIndex)"
                  :class="{ 
                    'expanded': isTimePayExpanded(groupIndex),
                    'warning': hasMissingTimePay(groupIndex)
                  }"
                >
                  <span>{{ getAverageTimePay(group) }}</span>
                  <span 
                    v-if="hasMissingTimePay(groupIndex)" 
                    class="time-pay-warning"
                    @click.stop="toggleTimePay(groupIndex)"
                  >
                    <v-icon size="16" color="#ef4444">mdi-alert</v-icon>
                  </span>
                </td>
                <td class="total-cell number-cell">{{ formatMoney(group.totalSugaMoney || 0) }}원</td>
                <td class="total-cell number-cell">{{ payrollDetails[groupIndex] ? formatMoney(payrollDetails[groupIndex].basicTotal) : '0' }}원</td>
                <td class="total-cell number-cell profit-cell" :class="{
                  'positive': payrollDetails[groupIndex] && calculateCenterProfit(group, groupIndex) > 0,
                  'negative': payrollDetails[groupIndex] && calculateCenterProfit(group, groupIndex) < 0
                }">
                  {{ payrollDetails[groupIndex] ? formatMoney(calculateCenterProfit(group, groupIndex)) : '0' }}원
                </td>
                <td class="input-cell pension-cell">
                  <div class="pension-input-wrapper">
                    <input 
                      type="checkbox"
                      v-model="settingsData[groupIndex].applyNationalPension"
                      @change="onNationalPensionToggle(groupIndex)"
                      :disabled="isConfirmed"
                      class="pension-checkbox"
                    />
                    <input 
                      v-if="settingsData[groupIndex].applyNationalPension"
                      type="text"
                      v-model="settingsData[groupIndex].yeonPay"
                      @input="onYeonPayInput($event, groupIndex)"
                      @blur="onYeonPayBlur($event, groupIndex)"
                      @focus="onYeonPayFocus($event, groupIndex)"
                      class="long-service-input"
                      placeholder="0"
                      :disabled="isConfirmed"
                    />
                  </div>
                </td>
                <td class="checkbox-cell">
                  <input 
                    type="checkbox"
                    v-model="settingsData[groupIndex].applyHealthInsurance" 
                    @change="checkUnsavedChanges"
                    :disabled="isConfirmed"
                  />
                </td>
                <td class="checkbox-cell employment-checkbox-cell">
                  <input 
                    type="checkbox"
                    :checked="settingsData[groupIndex].applyEmploymentInsurance"
                    @click.prevent="openEmploymentModal(groupIndex)"
                    :disabled="isConfirmed"
                  />
                  <span 
                    v-if="!settingsData[groupIndex].applyEmploymentInsurance && settingsData[groupIndex].applyUnemployment" 
                    class="unemployment-only-badge"
                    title="실업급여만 적용"
                  >실</span>
                </td>
                <td class="checkbox-cell">
                  <input 
                    type="checkbox"
                    v-model="settingsData[groupIndex].applyRetirement" 
                    @change="checkUnsavedChanges"
                    :disabled="isConfirmed"
                  />
                </td>
                <td class="checkbox-cell">
                  <input 
                    type="checkbox"
                    v-model="settingsData[groupIndex].applyHoliday" 
                    @change="checkUnsavedChanges"
                    :disabled="isConfirmed"
                  />
                </td>
                <td class="checkbox-cell">
                  <input 
                    type="checkbox"
                    v-model="settingsData[groupIndex].applyOvertime" 
                    @change="checkUnsavedChanges"
                    :disabled="isConfirmed"
                  />
                </td>
                <td class="checkbox-cell">
                  <input 
                    type="checkbox"
                    v-model="settingsData[groupIndex].applyMeal" 
                    @change="checkUnsavedChanges"
                    :disabled="isConfirmed"
                  />
                </td>
                <td class="input-cell pension-cell">
                  <div class="pension-input-wrapper">
                    <input 
                      type="checkbox"
                      v-model="settingsData[groupIndex].applyLongServicePay"
                      @change="onLongServicePayToggle(groupIndex)"
                      :disabled="isConfirmed"
                      class="pension-checkbox"
                    />
                    <input 
                      v-if="settingsData[groupIndex].applyLongServicePay"
                      type="text"
                      v-model="settingsData[groupIndex].longServicePay"
                      @input="onLongServiceInput($event, groupIndex)"
                      @blur="onLongServiceBlur($event, groupIndex)"
                      @focus="onLongServiceFocus($event, groupIndex)"
                      class="long-service-input"
                      placeholder="0"
                      :disabled="isConfirmed"
                    />
                  </div>
                </td>
                <td class="input-cell pension-cell">
                  <div class="pension-input-wrapper">
                    <input 
                      type="checkbox"
                      v-model="settingsData[groupIndex].applyBoninPay"
                      @change="onBoninPayToggle(groupIndex)"
                      :disabled="isConfirmed"
                      class="pension-checkbox"
                    />
                    <input 
                      v-if="settingsData[groupIndex].applyBoninPay"
                      type="text"
                      v-model="settingsData[groupIndex].boninPay"
                      @input="onBoninPayInput($event, groupIndex)"
                      @blur="onBoninPayBlur($event, groupIndex)"
                      @focus="onBoninPayFocus($event, groupIndex)"
                      @click="openBoninPayModal(groupIndex)"
                      class="long-service-input"
                      placeholder="클릭"
                      :disabled="isConfirmed"
                      readonly
                    />
                  </div>
                </td>
                <td class="input-cell pension-cell">
                  <div class="pension-input-wrapper">
                    <input 
                      type="checkbox"
                      v-model="settingsData[groupIndex].applyEmpEtc"
                      @change="onEmpEtcToggle(groupIndex)"
                      :disabled="isConfirmed"
                      class="pension-checkbox"
                    />
                    <input 
                      v-if="settingsData[groupIndex].applyEmpEtc"
                      type="text"
                      v-model="settingsData[groupIndex].empEtc"
                      @input="onEmpEtcInput($event, groupIndex)"
                      @blur="onEmpEtcBlur($event, groupIndex)"
                      @focus="onEmpEtcFocus($event, groupIndex)"
                      class="long-service-input"
                      placeholder="0"
                      :disabled="isConfirmed"
                    />
                  </div>
                </td>
                
              </tr>

              <!-- 시급 펼친 상세 행 -->
              <tr v-if="settingsData[groupIndex] && isTimePayExpanded(groupIndex)" class="detail-row time-pay-detail">
                <td colspan="15" class="detail-content-cell">
                  <div class="time-pay-details">
                    <div class="time-pay-content-wrapper">
                      <!-- 시급 설정 영역 -->
                      <div class="time-pay-left">
                        <div class="time-pay-header-text">
                          <v-icon size="18" color="#1e3a8a">mdi-account-heart</v-icon>
                          <span>수급자별 시급 설정</span>
                          <button 
                            class="btn-save-single" 
                            @click="saveSingleCaregiver(groupIndex)"
                            :disabled="isSaving || isConfirmed"
                          >
                            <v-progress-circular v-if="isSaving" indeterminate size="12" width="2" color="#1e3a8a" />
                            <v-icon v-else size="14">mdi-content-save</v-icon>
                            <span>저장</span>
                          </button>
                        </div>
                        <div class="time-pay-list">
                          <div v-for="(patient, pIdx) in group.patients" :key="pIdx" class="time-pay-item">
                            <!-- 왼쪽: 수급자 정보 -->
                            <div class="item-left">
                              <span class="patient-name">{{ patient.patientName }}</span>
                              <span class="suga-name">{{ getDisplaySugaName(patient) }}</span>
                            </div>
                            
                            <!-- 가운데: 서비스 정보 + 수가시간 조절 -->
                            <div class="item-center">
                              <span class="service-badge">{{ patient.serviceDays }}회</span>
                              <div 
                                class="hours-stepper"
                                :class="{ 'modified': isSugaHoursManuallyChanged(groupIndex, patient.patientCode, getSugaKey(patient)) }"
                              >
                                <button 
                                  class="step-btn"
                                  @click.stop="adjustSugaHoursInline(groupIndex, patient, -0.5)"
                                  :disabled="isConfirmed || getEffectiveSugaHours(groupIndex, patient) <= 0.5"
                                >−</button>
                                <span class="hours-value">
                                  {{ formatSugaHours(getEffectiveSugaHours(groupIndex, patient)) }}
                                </span>
                                <button 
                                  class="step-btn"
                                  @click.stop="adjustSugaHoursInline(groupIndex, patient, 0.5)"
                                  :disabled="isConfirmed || getEffectiveSugaHours(groupIndex, patient) >= 24"
                                >+</button>
                                <button 
                                  v-if="isSugaHoursManuallyChanged(groupIndex, patient.patientCode, getSugaKey(patient))"
                                  class="reset-btn"
                                  @click.stop="resetToAutoCalculation(groupIndex, patient)"
                                  :disabled="isConfirmed"
                                  title="자동계산으로 복원"
                                >↺</button>
                              </div>
                            </div>
                            
                            <!-- 오른쪽: 시급 입력 -->
                            <div class="item-right">
                              <input 
                                type="text"
                                :value="settingsData[groupIndex]?.timePays?.[patient.patientCode]?.[getSugaKey(patient)] || ''"
                                @input="onTimePayInput($event, groupIndex, patient.patientCode, getSugaKey(patient))"
                                @blur="onTimePayBlur($event, groupIndex, patient.patientCode, getSugaKey(patient))"
                                @focus="onTimePayFocus($event, groupIndex, patient.patientCode, getSugaKey(patient))"
                                class="wage-input"
                                :class="{ 'empty': !settingsData[groupIndex]?.timePays?.[patient.patientCode]?.[getSugaKey(patient)] }"
                                placeholder="시급"
                                :disabled="isConfirmed"
                              />
                              <span class="suffix">원</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- 간단 요약 정보 -->
                      <div class="employee-summary-section">
                        <div class="time-pay-header-text">
                          <v-icon size="18" color="#1e3a8a">mdi-account-cog</v-icon>
                          <span>급여 상세 정보</span>
                        </div>
                        <div class="employee-summary">
             
                          
                          <!-- 간단 정보 (항상 표시) -->
                          <div class="summary-quick-info">
                         
                            <div class="quick-info-item">
                              <v-icon size="14" color="#6b7280">mdi-clock-outline</v-icon>
                              <span>{{ formatSugaHours(getTotalSugaHours(group, groupIndex)) }}</span>
                            </div>
                            <div class="quick-info-item">
                              <v-icon size="14" color="#6b7280">mdi-counter</v-icon>
                              <span>{{ getTotalServiceDays(group) }}회</span>
                            </div>
                            <template v-if="getEmployeeFeatures(group, groupIndex).restrictions.length > 0">
                              <div 
                                class="quick-info-item warning clickable"
                                @click="openRestrictionsTab(groupIndex)"
                              >
                                <v-icon size="14" color="#f59e0b">mdi-alert</v-icon>
                                <span>제한사항 {{ getEmployeeFeatures(group, groupIndex).restrictions.length }}개</span>
                              </div>
                            </template>
                            <template v-if="group.isBoss">
                              <div class="quick-info-item boss-badge">
                                <v-icon size="14" color="#10b981">mdi-account-star</v-icon>
                                <span>대표자</span>
                              </div>
                            </template>
                            <button 
                              class="toggle-detail-btn"
                              @click="toggleEmployeeInfo(groupIndex)"
                              :class="{ 'active': isEmployeeInfoExpanded(groupIndex) }"
                            >
                              <span>{{ isEmployeeInfoExpanded(groupIndex) ? '급여 상세 정보 접기' : '급여 상세 정보 보기' }}</span>
                              <v-icon size="16">
                                {{ isEmployeeInfoExpanded(groupIndex) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                              </v-icon>
                            </button>
                          </div>

                          <!-- 급여 상세 정보 (펼쳤을 때만 표시) -->
                          <transition name="expand">
                            <div v-if="isEmployeeInfoExpanded(groupIndex)" class="employee-features-detailed">
                              <!-- 탭 네비게이션 -->
                              <v-tabs v-model="detailTab[groupIndex]" class="detail-tabs" density="compact">
                                <v-tab value="payroll">급여 상세</v-tab>
                                <v-tab value="info">직원 정보</v-tab>
                                <v-tab value="services">서비스 내역</v-tab>
                                <v-tab value="weekly">주차별 근무</v-tab>
                              </v-tabs>

                              <v-window v-model="detailTab[groupIndex]">
                                <!-- 급여 상세 탭 -->
                                <v-window-item value="payroll">
                                  <div v-if="payrollDetails[groupIndex]" class="payroll-details-tab">
                                  <!-- 급여 내역 -->
                                  <div class="payroll-section-compact">
                                    <div class="section-header-compact">
                                      <v-icon size="14" color="#1e3a8a">mdi-cash-multiple</v-icon>
                                      <span>급여 내역</span>
                                    </div>
                                    <div class="payroll-grid-compact">
                                      <template v-if="payrollDetails[groupIndex].isFamilyOnlyCaregiver">
                                        <!-- 가족 케어: 기본급에 주휴+연차 포함, 식대 지급 불가 -->
                                        <div class="payroll-item-compact full-width">
                                          <span class="label">기본급 (주휴·연차 포함)</span>
                                          <span class="value">{{ formatMoney(payrollDetails[groupIndex].basicTotal) }}원</span>
                                        </div>
                                      </template>
                                      <template v-else>
                                        <!-- 일반: 각 항목 별도 표시 -->
                                        <div class="payroll-item-compact">
                                          <span class="label">기본급</span>
                                          <span class="value">{{ formatMoney(payrollDetails[groupIndex].basicPay) }}원</span>
                                        </div>
                                        <div class="payroll-item-compact">
                                          <span class="label">주휴수당</span>
                                          <span class="value">{{ formatMoney(payrollDetails[groupIndex].holidayPay) }}원</span>
                                        </div>
                                        <div class="payroll-item-compact">
                                          <span class="label">연차수당</span>
                                          <span class="value">{{ formatMoney(payrollDetails[groupIndex].vacationPay) }}원</span>
                                        </div>
                                        <div class="payroll-item-compact">
                                          <span class="label">식대</span>
                                          <span class="value">{{ formatMoney(payrollDetails[groupIndex].mealAllowance) }}원</span>
                                        </div>
                                      </template>
                                      <div class="payroll-item-compact subtotal">
                                        <span class="label">급여 소계</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].basicTotal) }}원</span>
                                      </div>
                                    </div>
                                  </div>

                                  <!-- 수당 & 비과세 효과 섹션 (나란히 배치) -->
                                  <div class="payroll-row-flex">
                                    <!-- 수당 내역 -->
                                    <div class="payroll-section-compact">
                                      <div class="section-header-compact">
                                        <v-icon size="14" color="#059669">mdi-gift</v-icon>
                                        <span>수당</span>
                                      </div>
                                      <div class="payroll-grid-compact">
                                        <div class="payroll-item-compact">
                                          <span class="label">장기근속수당</span>
                                          <span class="value">{{ formatMoney(payrollDetails[groupIndex].longServicePay) }}원</span>
                                        </div>
                                        <div class="payroll-item-compact">
                                          <span class="label">중증수급자가산</span>
                                          <span class="value">{{ formatMoney(payrollDetails[groupIndex].severeCareAddition) }}원</span>
                                        </div>
                                        <div class="payroll-item-compact">
                                          <span class="label">휴일수당</span>
                                          <span class="value">{{ formatMoney(payrollDetails[groupIndex].holidayAllowance) }}원</span>
                                        </div>
                                        <div class="payroll-item-compact">
                                          <span class="label">연장수당</span>
                                          <span class="value">{{ formatMoney(payrollDetails[groupIndex].overtimeAllowance) }}원</span>
                                        </div>
                                        <div class="payroll-item-compact subtotal">
                                          <span class="label">수당 합계</span>
                                          <span class="value">{{ formatMoney(payrollDetails[groupIndex].longServicePay + payrollDetails[groupIndex].severeCareAddition + payrollDetails[groupIndex].holidayAllowance + payrollDetails[groupIndex].overtimeAllowance) }}원</span>
                                        </div>
                                      </div>
                                    </div>

                          
                                  </div>
                                  <div class="payroll-row-flex">

                                            <!-- 비과세 효과 -->
                                            <div class="payroll-section-compact tax-savings-card" v-if="payrollDetails[groupIndex].nonTaxableAmount > 0">
                                      <div class="section-header-compact">
                                        <v-icon size="14" color="#10b981">mdi-piggy-bank</v-icon>
                                        <span>비과세 효과</span>
                                      </div>
                                      <div class="payroll-grid-compact">
                                        <div class="payroll-item-compact">
                                          <span class="label">비과세 금액</span>
                                          <span class="value highlight">{{ formatMoney(payrollDetails[groupIndex].nonTaxableAmount) }}원</span>
                                        </div>
                                        <div class="payroll-item-compact savings-item">
                                          <span class="label">
                                            <v-icon size="12" color="#3b82f6">mdi-account</v-icon>
                                            실수령액 증가
                                          </span>
                                          <span class="value text-blue">+{{ formatMoney(payrollDetails[groupIndex].employeeTaxSavings) }}원</span>
                                        </div>
                                        <div class="payroll-item-compact savings-item">
                                          <span class="label">
                                            <v-icon size="12" color="#10b981">mdi-domain</v-icon>
                                            인건비 절감
                                          </span>
                                          <span class="value text-green">+{{ formatMoney(payrollDetails[groupIndex].centerTaxSavings) }}원</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <!-- 총 급여 -->
                                  <div class="payroll-total-compact">
                                    <span class="label">총 급여</span>
                                    <span class="value">{{ formatMoney(payrollDetails[groupIndex].totalPay) }}원</span>
                                  </div>

                                  <!-- 과세/비과세 구분 -->
                                  <div class="payroll-section-compact">
                                    <div class="section-header-compact">
                                      <v-icon size="14" color="#7c3aed">mdi-calculator</v-icon>
                                      <span>과세/비과세 구분</span>
                                    </div>
                                    <div class="payroll-grid-compact">
                                      <div class="payroll-item-compact">
                                        <span class="label">과세 금액</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].taxableAmount) }}원</span>
                                      </div>
                                      <div class="payroll-item-compact">
                                        <span class="label">비과세 금액</span>
                                        <span class="value ">{{ formatMoney(payrollDetails[groupIndex].nonTaxableAmount) }}원</span>
                                      </div>
                                      <div class="payroll-item-compact profit-highlight" :class="{
                                        'positive': calculateCenterProfit(group, groupIndex) > 0,
                                        'negative': calculateCenterProfit(group, groupIndex) < 0
                                      }">
                                        <span class="label">기관 수익</span>
                                        <span class="value">{{ formatMoney(calculateCenterProfit(group, groupIndex)) }}원</span>
                                      </div>
                                      <div class="payroll-item-compact retirement-cumulative">
                                        <span class="label">누적퇴직금</span>
                                        <span class="value">{{ formatMoney(cumulativeRetirement[group.name] || 0) }}원</span>
                                      </div>
                                    </div>
                                  </div>

                                  <!-- 근로자 공제 -->
                                  <div class="payroll-section-compact">
                                    <div class="section-header-compact">
                                      <v-icon size="14" color="#dc2626">mdi-minus-circle</v-icon>
                                      <span>근로자 공제</span>
                                    </div>
                                    <div class="payroll-grid-compact">
                                      <div
                                        class="payroll-item-compact"
                                        :class="{ 'has-deduction-tooltip': showAdminDeductionTooltip }"
                                        :title="deductionTooltip(groupIndex, 'nationalPension')"
                                      >
                                        <span class="label">국민연금</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].employeeDeductions.nationalPension) }}원</span>
                                      </div>
                                      <div
                                        class="payroll-item-compact"
                                        :class="{ 'has-deduction-tooltip': showAdminDeductionTooltip }"
                                        :title="deductionTooltip(groupIndex, 'healthInsurance')"
                                      >
                                        <span class="label">건강보험</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].employeeDeductions.healthInsurance) }}원</span>
                                      </div>
                                      <div
                                        class="payroll-item-compact"
                                        :class="{ 'has-deduction-tooltip': showAdminDeductionTooltip }"
                                        :title="deductionTooltip(groupIndex, 'longTermCare')"
                                      >
                                        <span class="label">장기요양보험</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].employeeDeductions.longTermCare) }}원</span>
                                      </div>
                                      <div
                                        class="payroll-item-compact"
                                        :class="{ 'has-deduction-tooltip': showAdminDeductionTooltip }"
                                        :title="deductionTooltip(groupIndex, 'employmentInsurance')"
                                      >
                                        <span class="label">고용보험</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].employeeDeductions.employmentInsurance) }}원</span>
                                      </div>
                                      <div
                                        class="payroll-item-compact"
                                        :class="{ 'has-deduction-tooltip': showAdminDeductionTooltip }"
                                        :title="deductionTooltip(groupIndex, 'incomeTax')"
                                      >
                                        <span class="label">소득세</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].employeeDeductions.incomeTax) }}원</span>
                                      </div>
                                      <div
                                        class="payroll-item-compact"
                                        :class="{ 'has-deduction-tooltip': showAdminDeductionTooltip }"
                                        :title="deductionTooltip(groupIndex, 'localTax')"
                                      >
                                        <span class="label">지방세</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].employeeDeductions.localTax) }}원</span>
                                      </div>
                                      <div
                                        v-if="payrollDetails[groupIndex].boninPayAmount > 0"
                                        class="payroll-item-compact"
                                        :class="{ 'has-deduction-tooltip': showAdminDeductionTooltip }"
                                        :title="deductionTooltip(groupIndex, 'boninPay')"
                                      >
                                        <span class="label">본인부담금</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].boninPayAmount) }}원</span>
                                      </div>
                                      <div
                                        v-if="payrollDetails[groupIndex].empEtcAmount > 0"
                                        class="payroll-item-compact"
                                        :class="{ 'has-deduction-tooltip': showAdminDeductionTooltip }"
                                        :title="deductionTooltip(groupIndex, 'empEtc')"
                                      >
                                        <span class="label">기타공제</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].empEtcAmount) }}원</span>
                                      </div>
                                      <div
                                        v-if="payrollDetails[groupIndex].supportDeduction > 0"
                                        class="payroll-item-compact"
                                        :class="{ 'has-deduction-tooltip': showAdminDeductionTooltip }"
                                        :title="deductionTooltip(groupIndex, 'supportDeduction')"
                                      >
                                        <span class="label">지원공제</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].supportDeduction) }}원</span>
                                      </div>
                                      <div
                                        class="payroll-item-compact subtotal"
                                        :class="{ 'has-deduction-tooltip': showAdminDeductionTooltip }"
                                        :title="deductionTooltip(groupIndex, 'totalDeduction')"
                                      >
                                        <span class="label">공제 합계</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].totalEmployeeDeduction) }}원</span>
                                      </div>
                                    </div>
                                  </div>

                                  <!-- 기관 공제 -->
                                  <div class="payroll-section-compact">
                                    <div class="section-header-compact">
                                      <v-icon size="14" color="#ea580c">mdi-domain</v-icon>
                                      <span>기관 공제</span>
                                    </div>
                                    <div class="payroll-grid-compact">
                                      <div
                                        class="payroll-item-compact"
                                        :class="{ 'has-deduction-tooltip': showAdminDeductionTooltip }"
                                        :title="centerTooltip(groupIndex, 'nationalPension')"
                                      >
                                        <span class="label">국민연금</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].centerDeductions.nationalPension) }}원</span>
                                      </div>
                                      <div
                                        class="payroll-item-compact"
                                        :class="{ 'has-deduction-tooltip': showAdminDeductionTooltip }"
                                        :title="centerTooltip(groupIndex, 'healthInsurance')"
                                      >
                                        <span class="label">건강보험</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].centerDeductions.healthInsurance) }}원</span>
                                      </div>
                                      <div
                                        class="payroll-item-compact"
                                        :class="{ 'has-deduction-tooltip': showAdminDeductionTooltip }"
                                        :title="centerTooltip(groupIndex, 'longTermCare')"
                                      >
                                        <span class="label">장기요양보험</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].centerDeductions.longTermCare) }}원</span>
                                      </div>
                                      <div
                                        class="payroll-item-compact"
                                        :class="{ 'has-deduction-tooltip': showAdminDeductionTooltip }"
                                        :title="centerTooltip(groupIndex, 'employmentInsurance')"
                                      >
                                        <span class="label">고용보험</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].centerDeductions.employmentInsurance) }}원</span>
                                      </div>
                                      <div
                                        class="payroll-item-compact"
                                        :class="{ 'has-deduction-tooltip': showAdminDeductionTooltip }"
                                        :title="centerTooltip(groupIndex, 'unemployment')"
                                      >
                                        <span class="label">실업급여</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].centerDeductions.unemployment) }}원</span>
                                      </div>
                                      <div
                                        class="payroll-item-compact"
                                        :class="{ 'has-deduction-tooltip': showAdminDeductionTooltip }"
                                        :title="centerTooltip(groupIndex, 'industrialAccident')"
                                      >
                                        <span class="label">산재보험</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].centerDeductions.industrialAccident) }}원</span>
                                      </div>
                                      <div
                                        class="payroll-item-compact"
                                        :class="{ 'has-deduction-tooltip': showAdminDeductionTooltip }"
                                        :title="centerTooltip(groupIndex, 'retirement')"
                                      >
                                        <span class="label">퇴직금</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].centerDeductions.retirement) }}원</span>
                                      </div>
                                      <div
                                        class="payroll-item-compact subtotal"
                                        :class="{ 'has-deduction-tooltip': showAdminDeductionTooltip }"
                                        :title="centerTooltip(groupIndex, 'totalCenter')"
                                      >
                                        <span class="label">공제 합계</span>
                                        <span class="value">{{ formatMoney(payrollDetails[groupIndex].totalCenterDeduction) }}원</span>
                                      </div>
                                    </div>
                                  </div>

                                  <!-- 실수령액 -->
                                  <div class="payroll-net-pay-compact">
                                    <span class="label">실수령액</span>
                                    <span class="value">{{ formatMoney(payrollDetails[groupIndex].netPay) }}원</span>
                                  </div>
                                </div>
                                <div v-else class="empty-state">
                                  <v-icon size="32" color="#9ca3af">mdi-calculator-variant-outline</v-icon>
                                  <p>급여 정보를 계산할 수 없습니다.</p>
                                </div>
                              </v-window-item>

                              <!-- 직원 정보 탭 -->
                              <v-window-item value="info">
                                <div class="features-columns">
                                  <!-- 왼쪽: 일반 정보 -->
                                  <div class="features-column info-column">
                                    <div class="column-header">
                                      <v-icon size="16" color="#3b82f6">mdi-information</v-icon>
                                      <span>일반 정보</span>
                                    </div>
                                    <div class="features-list">
                                      <div 
                                        v-for="(feature, fIdx) in getEmployeeFeatures(group, groupIndex).info" 
                                        :key="fIdx" 
                                        class="feature-item"
                                        :class="feature.type"
                                      >
                                        <v-icon :size="14" :color="feature.iconColor">{{ feature.icon }}</v-icon>
                                        <span>{{ feature.text }}</span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <!-- 오른쪽: 제한사항 -->
                                  <div class="features-column restriction-column">
                                    <div class="column-header">
                                      <v-icon size="16" color="#f59e0b">mdi-alert-circle</v-icon>
                                      <span>제한사항</span>
                                    </div>
                                    <div class="features-list">
                                      <template v-if="getEmployeeFeatures(group, groupIndex).restrictions.length > 0">
                                        <template v-for="(feature, fIdx) in getEmployeeFeatures(group, groupIndex).restrictions" :key="fIdx">
                                          <div 
                                            class="feature-item"
                                            :class="[feature.type, { 'clickable': feature.expandable, 'expanded': feature.expanded }]"
                                            @click="feature.expandable && toggleRestrictionDetail(groupIndex, fIdx)"
                                          >
                                            <v-icon :size="14" :color="feature.iconColor">{{ feature.icon }}</v-icon>
                                            <span>{{ feature.text }}</span>
                                            <v-icon v-if="feature.expandable" size="14" color="#f59e0b" class="expand-icon">
                                              {{ feature.expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                            </v-icon>
                                          </div>
                                          <template v-if="feature.details && feature.expanded">
                                            <div 
                                              v-for="(detail, dIdx) in feature.details"
                                              :key="`detail-${fIdx}-${dIdx}`"
                                              class="feature-detail"
                                            >
                                              <v-icon :size="12" :color="detail.iconColor">{{ detail.icon }}</v-icon>
                                              <span>{{ detail.text }}</span>
                                            </div>
                                          </template>
                                        </template>
                                      </template>
                                      <div v-else class="no-restrictions">
                                        <v-icon size="20" color="#10b981">mdi-check-circle</v-icon>
                                        <span>제한사항 없음</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </v-window-item>

                              <!-- 서비스 내역 탭 -->
                              <v-window-item value="services">
                                <div class="service-details-tab">
                                  <div v-if="individualServices[groupIndex] && individualServices[groupIndex].length > 0" class="service-table-wrapper">
                                    <table class="service-table-compact">
                                      <thead>
                                        <tr>
                                          <th>서비스일</th>
                                          <th>수급자명</th>
                                          <th>서비스시간</th>
                                          <th>수가명</th>
                                          <th>서비스시간</th>
                                          <th>수가금액</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr v-for="(service, sIdx) in individualServices[groupIndex]" :key="sIdx">
                                          <td>
                                            <div class="date-with-badge">
                                              {{ service.service_date }}
                                              <span v-if="isSunday(service.service_date)" class="sunday-badge-sm">일</span>
                                              <span v-if="getHolidayName(service.service_date)" class="holiday-badge-sm">
                                                {{ getHolidayName(service.service_date) }}
                                              </span>
                                            </div>
                                          </td>
                                          <td>{{ service.patient_name }}</td>
                                          <td>
                                            {{ service.start_time }}~{{ service.end_time }}
                                            <span v-if="service.is_lunch_time" class="meal-badge-sm">식대</span>
                                          </td>
                                          <td>{{ service.suga_name }}</td>
                                          <td>{{ formatTotalMinutes(service.service_minutes) }}</td>
                                          <td class="amount-cell">{{ formatMoney(service.suga_money) }}원</td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                  <div v-else class="empty-state">
                                    <v-icon size="32" color="#9ca3af">mdi-clipboard-text-outline</v-icon>
                                    <p>서비스 내역이 없습니다.</p>
                                  </div>
                                </div>
                              </v-window-item>

                              <!-- 주차별 근무내역 탭 -->
                              <v-window-item value="weekly">
                                <div class="weekly-details-tab">
                                  <div v-if="combinedWeeklyServices[groupIndex] && combinedWeeklyServices[groupIndex].length > 0" class="service-table-wrapper">
                                    <table class="weekly-table-compact">
                                      <thead>
                                        <tr>
                                          <th class="week-col">주차</th>
                                          <th class="date-col">서비스일</th>
                                          <th class="daily-col">일일 근무시간</th>
                                          <th class="total-col">주간 합계</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <template v-for="(weekGroup, wIdx) in combinedWeeklyServices[groupIndex]" :key="`week-${wIdx}`">
                                          <template v-for="(dailyItem, dIdx) in weekGroup.daily_items" :key="`daily-${wIdx}-${dIdx}`">
                                            <tr :class="{ 'week-first': dIdx === 0 }">
                                              <td v-if="dIdx === 0" :rowspan="weekGroup.daily_items.length" class="week-cell">
                                                <span class="week-badge-sm">{{ weekGroup.service_week }}주차</span>
                                              </td>
                                              <td class="date-cell">
                                                <div class="date-with-badge">
                                                  {{ dailyItem.service_date }}
                                                  <span v-if="isSunday(dailyItem.service_date)" class="sunday-badge-sm">일</span>
                                                  <span v-if="getHolidayName(dailyItem.service_date)" class="holiday-badge-sm">
                                                    {{ getHolidayName(dailyItem.service_date) }}
                                                  </span>
                                                </div>
                                              </td>
                                              <td class="hours-cell" :class="{ 'overtime-cell': dailyItem.daily_total_hours > 8 }">
                                                {{ dailyItem.daily_total_hours }}시간
                                              </td>
                                              <td v-if="dIdx === 0" :rowspan="weekGroup.daily_items.length" class="total-cell" :class="{ 'overtime-cell': weekGroup.weekly_total_hours > 40 }">
                                                {{ weekGroup.weekly_total_hours }}시간
                                              </td>
                                            </tr>
                                          </template>
                                        </template>
                                      </tbody>
                                    </table>
                                  </div>
                                  <div v-else class="empty-state">
                                    <v-icon size="32" color="#9ca3af">mdi-calendar-clock-outline</v-icon>
                                    <p>주차별 근무내역이 없습니다.</p>
                                  </div>
                                </div>
                              </v-window-item>
                            </v-window>
                          </div>
                        </transition>
                      </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="isLoadingMemberInfo" class="member-info-loading">
        <v-progress-circular indeterminate color="#1e3a8a" size="32"></v-progress-circular>
        <span>사원 급여 정보를 불러오는 중입니다...</span>
      </div>

      <div v-if="!isLoadingMemberInfo && memberInfoData.length === 0" class="member-info-empty">
        <v-icon size="32" color="#9ca3af">mdi-file-document-alert</v-icon>
        <p>아직 등록된 사원 급여 정보가 없습니다.</p>
      </div>

     

      <!-- 확정 확인 다이얼로그 -->
      <v-dialog v-model="showConfirmDialog" max-width="440px">
        <v-card class="confirm-dialog">
          <div class="confirm-dialog-header">
            <v-icon size="48" color="#10b981">mdi-lock-check</v-icon>
            <h3>급여 설정 확정</h3>
          </div>
          <div class="confirm-dialog-body">
            <p>급여 설정을 확정하시겠습니까?</p>
            <p>확정 후에는 급여 설정을 수정할 수 없습니다.</p>
          </div>
          <div class="confirm-dialog-footer">
            <button class="btn-cancel" @click="showConfirmDialog = false">취소</button>
            <button class="btn-confirm" @click="proceedConfirm">확정</button>
          </div>
        </v-card>
      </v-dialog>

      <!-- 해제 확인 다이얼로그 -->
      <v-dialog v-model="showReleaseDialog" max-width="440px">
        <v-card class="confirm-dialog">
          <div class="confirm-dialog-header warning">
            <v-icon size="48" color="#f59e0b">mdi-alert-circle</v-icon>
            <h3>확정 해제</h3>
          </div>
          <div class="confirm-dialog-body">
            <p>급여 설정 확정을 해제하시겠습니까?</p>
            <p>해제 후에는 다시 수정이 가능합니다.</p>
          </div>
          <div class="confirm-dialog-footer">
            <button class="btn-cancel" @click="showReleaseDialog = false">취소</button>
            <button class="btn-confirm warning" @click="proceedRelease">해제</button>
          </div>
        </v-card>
      </v-dialog>

      <!-- 본인부담금공제 모달 -->
      <v-dialog v-model="showBoninPayModal" max-width="480px">
        <v-card class="confirm-dialog bonin-pay-modal">
          <div class="confirm-dialog-header">
            <v-icon size="48" color="#8b5cf6">mdi-cash-minus</v-icon>
            <h3>본인부담금공제 설정</h3>
          </div>
          <div class="confirm-dialog-body">
            <p class="modal-description">가족요양 수가금액에서 본인부담금을 공제합니다.</p>
            <div class="bonin-pay-info">
              <div class="info-row">
                <span class="info-label">요양보호사</span>
                <span class="info-value">{{ currentBoninPayCaregiver?.name }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">가족요양 수가금액</span>
                <span class="info-value highlight">{{ formatMoney(currentBoninPayCaregiver?.familySugaMoney || 0) }}원</span>
              </div>
            </div>
            <div class="bonin-pay-options">
              <p class="options-title">공제 비율 선택</p>
              <div class="option-buttons">
                <button 
                  class="option-btn"
                  :class="{ 'active': selectedBoninRate === 15 }"
                  @click="selectBoninRate(15)"
                >
                  <span class="rate">15%</span>
                  <span class="amount">{{ formatMoney(calculateBoninAmount(15)) }}원</span>
                </button>
                <button 
                  class="option-btn"
                  :class="{ 'active': selectedBoninRate === 9 }"
                  @click="selectBoninRate(9)"
                >
                  <span class="rate">9%</span>
                  <span class="amount">{{ formatMoney(calculateBoninAmount(9)) }}원</span>
                </button>
                <button 
                  class="option-btn"
                  :class="{ 'active': selectedBoninRate === 6 }"
                  @click="selectBoninRate(6)"
                >
                  <span class="rate">6%</span>
                  <span class="amount">{{ formatMoney(calculateBoninAmount(6)) }}원</span>
                </button>
                <button 
                  class="option-btn no-deduction"
                  :class="{ 'active': selectedBoninRate === 0 }"
                  @click="selectBoninRate(0)"
                >
                  <span class="rate">0%</span>
                  <span class="amount">공제 안 함</span>
                </button>
              </div>
            </div>
          </div>
          <div class="confirm-dialog-footer">
            <button class="btn-cancel" @click="showBoninPayModal = false">취소</button>
            <button class="btn-confirm" @click="applyBoninPayRate" :disabled="selectedBoninRate === null">적용</button>
          </div>
        </v-card>
      </v-dialog>

      <!-- 고용보험 설정 모달 -->
      <v-dialog v-model="showEmploymentModal" max-width="520px">
        <v-card class="confirm-dialog employment-modal">
          <div class="confirm-dialog-header">
            <v-icon size="48" color="#3b82f6">mdi-briefcase-check</v-icon>
            <h3>고용보험 설정</h3>
          </div>
          <div class="confirm-dialog-body">
            <div class="employment-info">
              <div class="info-row">
                <span class="info-label">요양보호사</span>
                <span class="info-value">{{ currentEmploymentCaregiver?.name }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">만 나이</span>
                <span class="info-value" :class="{ 'highlight-warning': currentEmploymentCaregiver && currentEmploymentCaregiver.age >= 65 }">
                  {{ currentEmploymentCaregiver?.age }}세
                  <span v-if="currentEmploymentCaregiver && currentEmploymentCaregiver.age >= 65" class="age-note">(고용보험 납부 의무 없음)</span>
                </span>
              </div>
            </div>
            
            <div class="employment-options">
              <p class="options-title">적용할 항목을 선택하세요</p>
              
              <!-- 옵션 1: 고용보험 + 실업급여 모두 적용 -->
              <button 
                class="employment-option-btn"
                :class="{ 'active': selectedEmploymentOption === 'both' }"
                @click="selectedEmploymentOption = 'both'"
              >
                <div class="option-icon">
                  <v-icon size="24" :color="selectedEmploymentOption === 'both' ? '#3b82f6' : '#9ca3af'">mdi-check-all</v-icon>
                </div>
                <div class="option-content">
                  <p class="option-name">고용보험 + 실업급여 모두 적용</p>
                  <p class="option-desc">근로자: 고용보험료 공제 / 기관: 고용보험료 + 실업급여 공제</p>
                </div>
                <div class="option-check">
                  <v-icon v-if="selectedEmploymentOption === 'both'" size="20" color="#3b82f6">mdi-check-circle</v-icon>
                </div>
              </button>
              
              <!-- 옵션 2: 실업급여만 적용 -->
              <button 
                class="employment-option-btn"
                :class="{ 'active': selectedEmploymentOption === 'unemployment-only' }"
                @click="selectedEmploymentOption = 'unemployment-only'"
              >
                <div class="option-icon">
                  <v-icon size="24" :color="selectedEmploymentOption === 'unemployment-only' ? '#f59e0b' : '#9ca3af'">mdi-domain</v-icon>
                </div>
                <div class="option-content">
                  <p class="option-name">실업급여만 적용 (고용보험 미적용)</p>
                  <p class="option-desc">근로자: 공제 없음 / 기관: 실업급여만 공제</p>
                </div>
                <div class="option-check">
                  <v-icon v-if="selectedEmploymentOption === 'unemployment-only'" size="20" color="#f59e0b">mdi-check-circle</v-icon>
                </div>
              </button>
              
              <!-- 옵션 3: 모두 미적용 -->
              <button 
                class="employment-option-btn"
                :class="{ 'active': selectedEmploymentOption === 'none' }"
                @click="selectedEmploymentOption = 'none'"
              >
                <div class="option-icon">
                  <v-icon size="24" :color="selectedEmploymentOption === 'none' ? '#ef4444' : '#9ca3af'">mdi-close-circle</v-icon>
                </div>
                <div class="option-content">
                  <p class="option-name">모두 미적용</p>
                  <p class="option-desc">고용보험, 실업급여 모두 적용하지 않음</p>
                </div>
                <div class="option-check">
                  <v-icon v-if="selectedEmploymentOption === 'none'" size="20" color="#ef4444">mdi-check-circle</v-icon>
                </div>
              </button>
            </div>
          </div>
          <div class="confirm-dialog-footer">
            <button class="btn-cancel" @click="cancelEmploymentModal">취소</button>
            <button 
              class="btn-confirm" 
              @click="applyEmploymentInsurance"
              :disabled="!selectedEmploymentOption"
            >적용</button>
          </div>
        </v-card>
      </v-dialog>

      <!-- 수가시간 조정 모달 -->
      <v-dialog v-model="showSugaHoursModal" max-width="400px">
        <v-card class="confirm-dialog suga-hours-modal">
          <div class="confirm-dialog-header">
            <v-icon size="48" color="#1e3a8a">mdi-clock-edit-outline</v-icon>
            <h3>수가시간 조정</h3>
          </div>
          <div class="confirm-dialog-body">
            <div class="suga-hours-info">
              <div class="info-row">
                <span class="info-label">수급자</span>
                <span class="info-value">{{ currentSugaHoursPatient?.patientName }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">수가명</span>
                <span class="info-value">{{ currentSugaHoursPatient?.sugaName }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">원래 시간</span>
                <span class="info-value original">{{ formatSugaHours(currentSugaHoursPatient?.originalHours || 0) }}</span>
              </div>
            </div>
            
            <div class="suga-hours-selector">
              <p class="selector-title">수가시간 선택 (30분 단위)</p>
              <div class="hours-control">
                <button 
                  class="hours-btn minus"
                  @click="adjustSugaHours(-0.5)"
                  :disabled="selectedSugaHours <= 0.5"
                >
                  <v-icon size="24">mdi-minus</v-icon>
                </button>
                <div class="hours-display">
                  <span class="hours-value">{{ formatSugaHours(selectedSugaHours) }}</span>
                  <span 
                    v-if="selectedSugaHours !== currentSugaHoursPatient?.originalHours" 
                    class="hours-diff"
                    :class="{ 'positive': selectedSugaHours > (currentSugaHoursPatient?.originalHours || 0), 'negative': selectedSugaHours < (currentSugaHoursPatient?.originalHours || 0) }"
                  >
                    {{ selectedSugaHours > (currentSugaHoursPatient?.originalHours || 0) ? '+' : '' }}{{ formatSugaHours(selectedSugaHours - (currentSugaHoursPatient?.originalHours || 0)) }}
                  </span>
                </div>
                <button 
                  class="hours-btn plus"
                  @click="adjustSugaHours(0.5)"
                  :disabled="selectedSugaHours >= 24"
                >
                  <v-icon size="24">mdi-plus</v-icon>
                </button>
              </div>
              <div class="hours-presets">
                <button 
                  v-for="hours in [1, 2, 3, 4, 5, 6, 7, 8]" 
                  :key="hours"
                  class="preset-btn"
                  :class="{ 'active': selectedSugaHours === hours }"
                  @click="selectedSugaHours = hours"
                >
                  {{ hours }}시간
                </button>
              </div>
              <button 
                class="reset-btn"
                @click="selectedSugaHours = currentSugaHoursPatient?.originalHours || 0"
                :disabled="selectedSugaHours === currentSugaHoursPatient?.originalHours"
              >
                <v-icon size="16">mdi-refresh</v-icon>
                원래 시간으로 복원
              </button>
            </div>
          </div>
          <div class="confirm-dialog-footer">
            <button class="btn-cancel" @click="showSugaHoursModal = false">취소</button>
            <button 
              class="btn-confirm" 
              @click="applySugaHoursChange"
              :class="{ 'changed': selectedSugaHours !== currentSugaHoursPatient?.originalHours }"
            >
              {{ selectedSugaHours !== currentSugaHoursPatient?.originalHours ? '변경 적용' : '확인' }}
            </button>
          </div>
        </v-card>
      </v-dialog>

      <!-- 일괄 설정 모달 -->
      <SetModal
        v-model="showSetModal"
        :leb-ym="yearMonth"
        :sisul-code="store.state.user?.m_sisul_code"
        :current-data="memberInfoData"
        @apply-bulk-time-pay="handleBulkTimePay"
        @apply-bulk-time-pay-family="handleBulkTimePayFamily"
        @apply-bulk-time-pay-family-60="handleBulkTimePayFamily60"
        @apply-bulk-time-pay-family-90="handleBulkTimePayFamily90"
        @apply-bulk-holiday-allowance="handleBulkHolidayAllowance"
        @apply-bulk-overtime-allowance="handleBulkOvertimeAllowance"
        @apply-bulk-retirement="handleBulkRetirement"
        @apply-bulk-meal-allowance="handleBulkMealAllowance"
        @apply-bulk-long-service-auto-calculate="handleBulkLongServiceAutoCalculate"
        @apply-bulk-long-service-clear="handleBulkLongServiceClear"
        @apply-bulk-labor-day-allowance="handleBulkLaborDayAllowance"
        @apply-all-previous-settings="handleApplyAllPreviousSettings"
        @reset-all-settings="handleResetAllSettings"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import SetModal from '@/components/wage/SetModal.vue';
import WageWholeVersion from '@/components/wage/WageWholeVersion.vue';
import WageEmployeeList from './WageEmployeeList.vue';
import WageEmployeeModal from './WageEmployeeModal.vue';
import WageEmployeeCalendar from './WageEmployeeCalendar.vue';
import {
  shouldShowAdminDeductionTooltip,
  buildDeductionTooltip,
  buildCenterTooltip,
} from './payrollTooltipUtils';

interface PatientInfo {
  patientCode: string;
  patientName: string;
  sugaCode: string;
  sugaName: string;
  sugaMinutesKey: number | null;  // 270분초과 수가의 경우 수가시간(30분 단위)
  timePay: string;
  serviceDays: number;
  sugaHours: number;
  totalSugaMoney: number;
  lunchTimeCount: number;
}

interface GroupedMember {
  empCode: string;  // hh_emp 테이블의 emp_code
  name: string;
  birth: string;
  gender: string;
  license: string;
  workStartDate: string;
  workEndDate: string;
  patients: PatientInfo[];
  originalItems: any[];
  totalSugaMoney: number;
  totalLunchTimeCount: number;
  isBoss: boolean;
}

interface SettingsItem {
  applyNationalPension: boolean;
  yeonPay: string;
  applyBoninPay: boolean;
  boninPay: string;
  applyHealthInsurance: boolean;
  applyEmploymentInsurance: boolean;
  applyUnemployment: boolean;
  applyRetirement: boolean;
  applyHoliday: boolean;
  applyOvertime: boolean;
  applyMeal: boolean;
  applyLongServicePay: boolean;
  longServicePay: string;
  applyEtcAllowance: boolean;
  etcAllowance: string;
  applyBosuEduPay: boolean;
  bosuEduPay: string;
  applyEmpEtc: boolean;
  empEtc: string;
  timePays: Record<string, Record<string, string>>;
  customSugaHours: Record<string, Record<string, number>>;
  sugaHoursChanged: boolean;
  _hasYeonPaySetting?: boolean;  // DB에 국민연금 설정이 저장된 적이 있는지 플래그
}

const props = defineProps<{
  stepCompleted: boolean;
  isConfirmed?: boolean;
  isConfirming?: boolean;
  yearMonth?: string;
}>();

const emit = defineEmits<{
  (e: 'open-settings'): void;
  (e: 'confirm'): void;
  (e: 'release'): void;
}>();

const store = useStore();

const isConfirmed = computed(() => !!props.isConfirmed);
const isConfirming = computed(() => !!props.isConfirming);

const showAdminDeductionTooltip = computed(() =>
  shouldShowAdminDeductionTooltip(store.getters.isAdmin, store.getters.isImpersonating)
);

const deductionTooltip = (groupIndex: number, field: string): string => {
  if (!showAdminDeductionTooltip.value) return '';
  return buildDeductionTooltip(field, payrollDetails.value[groupIndex], settingsData.value[groupIndex]);
};

const centerTooltip = (groupIndex: number, field: string): string => {
  if (!showAdminDeductionTooltip.value) return '';
  return buildCenterTooltip(field, payrollDetails.value[groupIndex], settingsData.value[groupIndex]);
};

// 확정 가능 여부 계산 (내부 로직)
const stepCompleted = computed(() => {
  // props로 전달된 값이 있으면 우선 사용 (DB 확정 상태)
  if (props.stepCompleted) return true;
  
  // 데이터가 없으면 false
  if (!memberInfoData.value || memberInfoData.value.length === 0) {
    return false;
  }
  
  // 모든 요양보호사의 모든 수급자에 대해 시급이 입력되어 있는지 확인
  for (let groupIndex = 0; groupIndex < groupedMemberData.value.length; groupIndex++) {
    const group = groupedMemberData.value[groupIndex];
    const settings = settingsData.value[groupIndex];
    
    if (!settings || !settings.timePays) {
      return false;
    }
    
    // 각 수급자별로 시급이 입력되어 있는지 확인
    for (const patient of group.patients) {
      // getSugaKey 함수와 동일한 로직 사용 (hoursKey 포함)
      const hoursKey = patient.sugaHours ? `_h${patient.sugaHours}` : '';
      const sugaKey = patient.sugaMinutesKey != null 
        ? `${patient.sugaCode}_${patient.sugaMinutesKey}${hoursKey}` 
        : `${patient.sugaCode}${hoursKey}`;
      
      const timePay = settings.timePays[patient.patientCode]?.[sugaKey];
      if (!timePay || !String(timePay).trim()) {
        return false;
      }
      
      // 시급이 0보다 큰지 확인
      const numericTimePay = parseFloat(String(timePay).replace(/[^\d]/g, '')) || 0;
      if (numericTimePay <= 0) {
        return false;
      }
    }
  }
  
  return true;
});

// 확정/해제 확인 다이얼로그 상태
const showConfirmDialog = ref(false);
const showReleaseDialog = ref(false);

// 본인부담금공제 모달 상태
const showBoninPayModal = ref(false);
const currentBoninPayGroupIndex = ref<number | null>(null);
const currentBoninPayCaregiver = ref<{ name: string; familySugaMoney: number } | null>(null);
const selectedBoninRate = ref<number | null>(null);

// 고용보험 모달 상태
const showEmploymentModal = ref(false);
const currentEmploymentGroupIndex = ref<number | null>(null);
const currentEmploymentCaregiver = ref<{ name: string; age: number } | null>(null);
const selectedEmploymentOption = ref<'both' | 'unemployment-only' | 'none' | null>(null);

// 수가시간 조정 모달 상태
const showSugaHoursModal = ref(false);
const currentSugaHoursGroupIndex = ref<number | null>(null);
const currentSugaHoursPatient = ref<{
  patientCode: string;
  patientName: string;
  sugaKey: string;
  sugaName: string;
  originalHours: number;
} | null>(null);
const selectedSugaHours = ref<number>(0);

// 수가시간 인라인 드롭다운 상태 (groupIndex_patientCode_sugaKey 형태로 저장)
const openSugaHoursDropdown = ref<string | null>(null);

// 일괄 설정 모달 상태
const showSetModal = ref(false);

// 전체급여상세 표시 상태
const showWholeVersion = ref(false);

// 사원 상세 모달 상태 (WageEmployeeModal용)
const showEmployeeModal = ref(false);
const selectedEmployeeIndex = ref(0);
const selectedEmployee = ref<GroupedMember | null>(null);

// Member Info 데이터
const memberInfoData = ref<any[]>([]);
const memberJoinDetailData = ref<any[]>([]); // 중증수급자 가산 포함 데이터
const isLoadingMemberInfo = ref(false);
const isSaving = ref(false);

// 펼쳐진 시급 행 관리
const expandedTimePay = ref(new Set<number>());

// 제한사항 상세 펼침 관리
const expandedRestrictions = ref<Record<number, Set<number>>>({});

// 직원 급여 상세 정보 펼침 관리
const expandedEmployeeInfo = ref(new Set<number>());

// 급여 상세 정보 탭 관리
const detailTab = ref<Record<number, string>>({});

// 서비스 내역 데이터
const individualServices = ref<Record<number, any[]>>({});

// 중증수급자 가산 일자별 데이터
const severeCareAdditionDaily = ref<Record<number, any[]>>({});

// 주차별 근무내역 데이터
const combinedWeeklyServices = ref<Record<number, any[]>>({});

// 급여 상세 데이터
const payrollDetails = ref<Record<number, any>>({});

// 누적 퇴직금 데이터 (직원명 기준)
const cumulativeRetirement = ref<Record<string, number>>({});

// 설정 데이터
const settingsData = ref<Record<number, SettingsItem>>({});

// 세액 및 최저시급 데이터
const taxData = ref<any>({});

// 소득세 간이세액표 데이터
const sojiData = ref<any[]>([]);

// 공휴일 데이터
const holidaysData = ref<Record<string, string>>({});

// 저장되지 않은 변경사항 추적
const hasUnsavedChanges = ref(false);

// 원래 설정 데이터 (변경사항 비교용)
const originalSettingsData = ref<Record<number, string>>({});

// 변경사항 확인 함수
const checkUnsavedChanges = () => {
  let hasChanges = false;
  for (const groupIndex of Object.keys(settingsData.value)) {
    const idx = Number(groupIndex);
    const current = JSON.stringify(settingsData.value[idx]);
    const original = originalSettingsData.value[idx];
    if (current !== original) {
      hasChanges = true;
      break;
    }
  }
  hasUnsavedChanges.value = hasChanges;
};

// 원래 설정 저장
const saveOriginalSettings = () => {
  for (const groupIndex of Object.keys(settingsData.value)) {
    const idx = Number(groupIndex);
    originalSettingsData.value[idx] = JSON.stringify(settingsData.value[idx]);
  }
};

// 시급 행 펼치기/접기
const toggleTimePay = (index: number) => {
  if (expandedTimePay.value.has(index)) {
    expandedTimePay.value.delete(index);
  } else {
    expandedTimePay.value.add(index);
  }
};

// 시급 행이 펼쳐져 있는지 확인
const isTimePayExpanded = (index: number) => {
  return expandedTimePay.value.has(index);
};

// 제한사항 상세 토글
const toggleRestrictionDetail = (groupIndex: number, restrictionIndex: number) => {
  if (!expandedRestrictions.value[groupIndex]) {
    expandedRestrictions.value[groupIndex] = new Set();
  }
  
  if (expandedRestrictions.value[groupIndex].has(restrictionIndex)) {
    expandedRestrictions.value[groupIndex].delete(restrictionIndex);
  } else {
    expandedRestrictions.value[groupIndex].add(restrictionIndex);
  }
};

// 직원 급여 상세 정보 토글
const toggleEmployeeInfo = (groupIndex: number) => {
  if (expandedEmployeeInfo.value.has(groupIndex)) {
    expandedEmployeeInfo.value.delete(groupIndex);
  } else {
    expandedEmployeeInfo.value.add(groupIndex);
    // 탭 초기값 설정 (급여 상세가 기본)
    if (!detailTab.value[groupIndex]) {
      detailTab.value[groupIndex] = 'payroll';
    }
  }
};

// 직원 급여 상세 정보가 펼쳐져 있는지 확인
const isEmployeeInfoExpanded = (groupIndex: number) => {
  return expandedEmployeeInfo.value.has(groupIndex);
};

// 시급 미입력 여부 확인
const hasMissingTimePay = (groupIndex: number) => {
  const settings = settingsData.value[groupIndex];
  const group = groupedMemberData.value[groupIndex];

  if (!settings || !group) return true;

  return group.patients.some((patient) => {
    // 270분초과의 경우 수가시간별 키 사용
    const sugaKey = getSugaKey(patient);
    const value = settings.timePays?.[patient.patientCode]?.[sugaKey];
    return !value || !String(value).trim();
  });
};

// 제한사항 탭 열기 (클릭 시 급여 상세 정보 열고 직원 정보 탭으로 이동)
const openRestrictionsTab = (groupIndex: number) => {
  // 급여 상세 정보가 닫혀있으면 열기
  if (!expandedEmployeeInfo.value.has(groupIndex)) {
    expandedEmployeeInfo.value.add(groupIndex);
  }
  // 직원 정보 탭으로 이동
  detailTab.value[groupIndex] = 'info';
};

// 사원별로 그룹화된 데이터
const groupedMemberData = computed<GroupedMember[]>(() => {
  if (!memberInfoData.value || memberInfoData.value.length === 0) return [];

  const groups: Record<string, GroupedMember> = {};

  memberInfoData.value.forEach((item) => {
    const key = `${item.lemi_name}_${item.lemi_birth}`;
    
    if (!groups[key]) {
      groups[key] = {
        empCode: item.emp_code || '',  // hh_emp 테이블의 emp_code
        name: item.lemi_name,
        birth: item.lemi_birth,
        gender: item.lemi_gender,
        license: item.lemi_license,
        workStartDate: item.lemi_work_start_date,
        workEndDate: item.lemi_work_end_date,
        patients: [],
        originalItems: [],
        totalSugaMoney: 0,
        totalLunchTimeCount: 0,
        isBoss: item.lem_isboss === 'Y'
      };
    }

    // 수가 금액 누적
    const sugaMoney = parseFloat(String(item.total_suga_money || 0).replace(/[^\d]/g, '')) || 0;
    groups[key].totalSugaMoney += sugaMoney;
    
    // 식대 카운트 누적 (12:00 ~ 13:00 사이 근무한 일수)
    const lunchCount = Number(item.lunch_time_count) || 0;
    groups[key].totalLunchTimeCount += lunchCount;

    // 270분초과 수가의 경우 수가시간(30분 단위)별로 분리된 키 사용
    const sugaMinutesKey = item.suga_minutes_key != null ? Number(item.suga_minutes_key) : null;
    
    groups[key].patients.push({
      patientCode: item.lemi_patient_code,
      patientName: item.patient_name || item.lemi_patient_code,
      sugaCode: item.lemi_suga_code,
      sugaName: item.suga_name || '-',
      sugaMinutesKey: sugaMinutesKey,
      timePay: item.lemi_time_pay || '',
      serviceDays: item.service_days || 0,
      sugaHours: parseFloat(String(item.suga_hours || 0).replace(/[^\d.-]/g, '')) || 0,
      totalSugaMoney: sugaMoney,
      lunchTimeCount: lunchCount
    });
    groups[key].originalItems.push(item);
  });

  const result = Object.values(groups);
  

  return result;
});

// 수가 키 생성 함수 (원래 수가시간도 포함하여 같은 수급자/수가코드라도 구분)
const getSugaKey = (patient: PatientInfo): string => {
  // 원래 수가시간을 키에 포함하여 중복 카드 구분
  const hoursKey = patient.sugaHours ? `_h${patient.sugaHours}` : '';
  
  if (patient.sugaMinutesKey != null) {
    return `${patient.sugaCode}_${patient.sugaMinutesKey}${hoursKey}`;
  }
  return `${patient.sugaCode}${hoursKey}`;
};

// 수가명 표시 (270분초과의 경우 수가시간도 표시)
const getDisplaySugaName = (patient: PatientInfo): string => {
  if (patient.sugaMinutesKey != null && patient.sugaName?.includes('270분초과')) {
    const hours = patient.sugaMinutesKey / 60;
    const displayHours = hours % 1 === 0 ? `${hours}시간` : `${hours.toFixed(1)}시간`;
    return `${patient.sugaName} (${displayHours})`;
  }
  return patient.sugaName || '-';
};

// 설정 데이터 초기화
const initializeSettingsData = () => {
  groupedMemberData.value.forEach((group, index) => {
    if (!settingsData.value[index]) {
      const firstItem = group.originalItems[0];
      
      // 수급자별 시급 초기화 (270분초과는 수가시간별로 구분)
      const timePays: Record<string, Record<string, string>> = {};
      group.patients.forEach((patient) => {
        if (!timePays[patient.patientCode]) {
          timePays[patient.patientCode] = {};
        }
        // 기존 시급 값 포맷팅
        let timePay = patient.timePay || '';
        if (timePay) {
          const numericValue = parseFloat(String(timePay).replace(/[^\d]/g, '')) || 0;
          if (numericValue > 0) {
            timePay = new Intl.NumberFormat('ko-KR').format(numericValue);
          }
        }
        // 270분초과의 경우 수가시간별 키 사용
        const sugaKey = getSugaKey(patient);
        timePays[patient.patientCode][sugaKey] = timePay;
      });

      // 장기근속수당 포맷팅
      let longServicePay = firstItem?.lemi_long_service_pay || '';
      let applyLongServicePay = false;
      if (longServicePay) {
        const numericValue = parseFloat(String(longServicePay).replace(/[^\d]/g, '')) || 0;
        if (numericValue > 0) {
          longServicePay = new Intl.NumberFormat('ko-KR').format(numericValue);
          applyLongServicePay = true;
        }
      }

      // 국민연금 포맷팅
      // DB에 lemi_yeon_pay 필드가 존재하면 (빈 문자열이든 값이 있든) 이미 저장된 적이 있음
      // lemi_yeon_pay가 undefined가 아니면 이미 한번 저장된 설정임
      const hasYeonPaySetting = firstItem?.lemi_yeon_pay !== undefined && firstItem?.lemi_yeon_pay !== null;
      let yeonPay = firstItem?.lemi_yeon_pay || '';
      let applyNationalPension = false;
      if (yeonPay) {
        const numericValue = parseFloat(String(yeonPay).replace(/[^\d]/g, '')) || 0;
        if (numericValue > 0) {
          yeonPay = new Intl.NumberFormat('ko-KR').format(numericValue);
          applyNationalPension = true;
        }
      }

      // 본인부담금공제 포맷팅
      let boninPay = firstItem?.lemi_bonin_pay || '';
      let applyBoninPay = false;
      if (boninPay) {
        const numericValue = parseFloat(String(boninPay).replace(/[^\d]/g, '')) || 0;
        if (numericValue > 0) {
          boninPay = new Intl.NumberFormat('ko-KR').format(numericValue);
          applyBoninPay = true;
        }
      }

      // 기타공제 포맷팅
      let empEtc = firstItem?.lemi_emp_etc || '';
      let applyEmpEtc = false;
      if (empEtc) {
        const numericValue = parseFloat(String(empEtc).replace(/[^\d]/g, '')) || 0;
        if (numericValue > 0) {
          empEtc = new Intl.NumberFormat('ko-KR').format(numericValue);
          applyEmpEtc = true;
        }
      }

      // 기타수당 포맷팅
      let etcAllowance = firstItem?.lemi_etc_allowance || '';
      let applyEtcAllowance = false;
      if (etcAllowance) {
        const numericValue = parseFloat(String(etcAllowance).replace(/[^\d]/g, '')) || 0;
        if (numericValue > 0) {
          etcAllowance = new Intl.NumberFormat('ko-KR').format(numericValue);
          applyEtcAllowance = true;
        }
      }

      // 실업급여 적용 여부 확인 (lemi_sil_pay에 값이 있으면 적용)
      const silPay = firstItem?.lemi_sil_pay || '';
      const hasSilPay = silPay && parseFloat(String(silPay).replace(/[^\d]/g, '')) > 0;
      // 고용보험 적용 시 실업급여도 기본 적용, 또는 lemi_sil_pay에 값이 있으면 적용
      const applyUnemployment = (firstItem?.lemi_apply_go === 'Y') || hasSilPay;
      
      // DB에서 수동 조정된 수가시간 복원 (JSON 파싱)
      let customSugaHours: Record<string, Record<string, number>> = {};
      let sugaHoursChanged = false;
      if (firstItem?.lemi_custom_suga_hours) {
        try {
          customSugaHours = JSON.parse(firstItem.lemi_custom_suga_hours);
          sugaHoursChanged = Object.keys(customSugaHours).length > 0;
        } catch (e) {
          console.warn('customSugaHours 파싱 실패:', e);
          customSugaHours = {};
        }
      }
      
      // DB에 저장된 값을 그대로 사용 (Step2 확정 시 디폴트 값이 설정됨)
      settingsData.value[index] = {
        applyNationalPension: applyNationalPension,
        yeonPay: yeonPay,
        applyBoninPay: applyBoninPay,
        boninPay: boninPay,
        applyHealthInsurance: firstItem?.lemi_apply_gun === 'Y',
        applyEmploymentInsurance: firstItem?.lemi_apply_go === 'Y',
        applyUnemployment: applyUnemployment,
        applyRetirement: firstItem?.lemi_apply_out_pay === 'Y',
        applyHoliday: firstItem?.lemi_apply_holiday_pay === 'Y',
        applyOvertime: firstItem?.lemi_apply_overtime_pay === 'Y',
        applyMeal: firstItem?.lemi_apply_lunch_pay === 'Y',
        applyLongServicePay: applyLongServicePay,
        longServicePay: longServicePay,
        applyEtcAllowance: applyEtcAllowance,
        etcAllowance: etcAllowance,
        applyBosuEduPay: false,
        bosuEduPay: '',
        applyEmpEtc: applyEmpEtc,
        empEtc: empEtc,
        timePays: timePays,
        customSugaHours: customSugaHours,
        sugaHoursChanged: sugaHoursChanged,
        _hasYeonPaySetting: hasYeonPaySetting  // DB에 국민연금 설정이 저장된 적이 있는지 플래그
      };

      // 가족요양만 진행하는 요양보호사의 경우, 수가시간 60시간 미만이면 체크박스 자동 해제
      // 단, DB에 이미 설정된 값이 있으면 사용자 선택을 존중하여 유지
      const isFamilyOnly = isAllFamilyCaregiver(group);
      const totalSugaHours = getTotalSugaHours(group, index);
      
      // DB에 저장된 값이 없는 경우에만 자동 해제 (첫 로드 시)
      const hasExistingYeonPay = firstItem?.lemi_yeon_pay && parseFloat(String(firstItem.lemi_yeon_pay).replace(/[^\d]/g, '')) > 0;
      const hasExistingHealthInsurance = firstItem?.lemi_apply_gun === 'Y';
      const hasExistingMeal = firstItem?.lemi_apply_lunch_pay === 'Y';
      
      if (isFamilyOnly && totalSugaHours < 60) {
        // DB에 저장된 값이 없는 경우에만 기본값으로 해제
        if (!hasExistingYeonPay) {
          settingsData.value[index].applyNationalPension = false;
          settingsData.value[index].yeonPay = '';
        }
        if (!hasExistingHealthInsurance) {
          settingsData.value[index].applyHealthInsurance = false;
        }
        if (!hasExistingMeal) {
          settingsData.value[index].applyMeal = false;
        }
      }

      // 대표자인 경우, 고용보험과 퇴직금 체크박스 자동 해제
      // 단, DB에 이미 설정된 값이 있으면 사용자 선택을 존중하여 유지
      const isBoss = firstItem?.lem_isboss === 'Y';
      const hasExistingEmploymentInsurance = firstItem?.lemi_apply_go === 'Y';
      const hasExistingRetirement = firstItem?.lemi_apply_out_pay === 'Y';
      
      if (isBoss) {
        // DB에 저장된 값이 없는 경우에만 기본값으로 해제
        if (!hasExistingEmploymentInsurance) {
          settingsData.value[index].applyEmploymentInsurance = false;
        }
        if (!hasExistingRetirement) {
          settingsData.value[index].applyRetirement = false;
        }
      }
    }
  });
};

// 시급 입력 이벤트
const onTimePayInput = (event: Event, groupIndex: number, patientCode: string, sugaCode: string) => {
  const input = event.target as HTMLInputElement;
  let value = input.value;
  value = value.replace(/[^\d]/g, '');
  if (value.length > 9) {
    value = value.slice(0, 9);
  }
  input.value = value;
  
  if (!settingsData.value[groupIndex].timePays[patientCode]) {
    settingsData.value[groupIndex].timePays[patientCode] = {};
  }
  settingsData.value[groupIndex].timePays[patientCode][sugaCode] = value;
  checkUnsavedChanges();
};

const onTimePayFocus = (event: FocusEvent, groupIndex: number, patientCode: string, sugaCode: string) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/,/g, '');
  input.value = numericValue;
};

const onTimePayBlur = (event: FocusEvent, groupIndex: number, patientCode: string, sugaCode: string) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/[^\d]/g, '');
  
  if (numericValue) {
    const formattedValue = new Intl.NumberFormat('ko-KR').format(parseInt(numericValue));
    input.value = formattedValue;
    
    if (!settingsData.value[groupIndex].timePays[patientCode]) {
      settingsData.value[groupIndex].timePays[patientCode] = {};
    }
    settingsData.value[groupIndex].timePays[patientCode][sugaCode] = formattedValue;
  } else {
    input.value = '';
    if (settingsData.value[groupIndex].timePays[patientCode]) {
      settingsData.value[groupIndex].timePays[patientCode][sugaCode] = '';
    }
  }
  checkUnsavedChanges();
};

// 장기근속수당 체크박스 토글
const onLongServicePayToggle = (groupIndex: number) => {
  const settings = settingsData.value[groupIndex];
  if (!settings) return;
  
  if (settings.applyLongServicePay) {
    // 체크박스 활성화 시 값 초기화
    settings.longServicePay = '0';
  } else {
    // 체크박스 비활성화 시 값 초기화
    settings.longServicePay = '';
  }
  checkUnsavedChanges();
};

// 장기근속수당 입력 이벤트
const onLongServiceInput = (event: Event, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  let value = input.value;
  value = value.replace(/[^\d]/g, '');
  if (value.length > 9) {
    value = value.slice(0, 9);
  }
  input.value = value;
  settingsData.value[groupIndex].longServicePay = value;
  checkUnsavedChanges();
};

const onLongServiceFocus = (event: FocusEvent, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/,/g, '');
  // 값이 0이면 빈 문자열로 표시
  input.value = numericValue === '0' ? '' : numericValue;
};

const onLongServiceBlur = (event: FocusEvent, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/[^\d]/g, '');
  
  if (numericValue) {
    const formattedValue = new Intl.NumberFormat('ko-KR').format(parseInt(numericValue));
    input.value = formattedValue;
    settingsData.value[groupIndex].longServicePay = formattedValue;
  } else {
    input.value = '';
    settingsData.value[groupIndex].longServicePay = '';
  }
  checkUnsavedChanges();
};

// 국민연금 입력 이벤트
const onYeonPayInput = (event: Event, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  let value = input.value;
  value = value.replace(/[^\d]/g, '');
  if (value.length > 9) {
    value = value.slice(0, 9);
  }
  input.value = value;
  settingsData.value[groupIndex].yeonPay = value;
  checkUnsavedChanges();
};

const onYeonPayFocus = (event: FocusEvent, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/,/g, '');
  // 값이 0이면 빈 문자열로 표시
  input.value = numericValue === '0' ? '' : numericValue;
};

const onYeonPayBlur = (event: FocusEvent, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/[^\d]/g, '');
  
  if (numericValue) {
    const formattedValue = new Intl.NumberFormat('ko-KR').format(parseInt(numericValue));
    input.value = formattedValue;
    settingsData.value[groupIndex].yeonPay = formattedValue;
  } else {
    input.value = '';
    settingsData.value[groupIndex].yeonPay = '';
  }
  checkUnsavedChanges();
};

// 국민연금 체크박스 토글
const onNationalPensionToggle = (groupIndex: number) => {
  const settings = settingsData.value[groupIndex];
  if (!settings) return;
  
  if (settings.applyNationalPension) {
    // 체크박스 활성화 시 국민연금 자동 계산
    const payroll = payrollDetails.value[groupIndex];
    if (payroll && payroll.suggestedNationalPension > 0) {
      settings.yeonPay = new Intl.NumberFormat('ko-KR').format(payroll.suggestedNationalPension);
    } else {
      settings.yeonPay = '0';
    }
  } else {
    // 체크박스 비활성화 시 값 초기화
    settings.yeonPay = '';
  }
  checkUnsavedChanges();
};

// 본인부담금공제 입력 이벤트
const onBoninPayInput = (event: Event, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  let value = input.value;
  value = value.replace(/[^\d]/g, '');
  if (value.length > 9) {
    value = value.slice(0, 9);
  }
  input.value = value;
  settingsData.value[groupIndex].boninPay = value;
  checkUnsavedChanges();
};

const onBoninPayFocus = (event: FocusEvent, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/,/g, '');
  // 값이 0이면 빈 문자열로 표시
  input.value = numericValue === '0' ? '' : numericValue;
};

const onBoninPayBlur = (event: FocusEvent, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/[^\d]/g, '');
  
  if (numericValue) {
    const formattedValue = new Intl.NumberFormat('ko-KR').format(parseInt(numericValue));
    input.value = formattedValue;
    settingsData.value[groupIndex].boninPay = formattedValue;
  } else {
    input.value = '';
    settingsData.value[groupIndex].boninPay = '';
  }
  checkUnsavedChanges();
};

// 본인부담금공제 체크박스 토글
const onBoninPayToggle = (groupIndex: number) => {
  const settings = settingsData.value[groupIndex];
  if (!settings) return;
  
  if (settings.applyBoninPay) {
    // 체크박스 활성화 시 모달 열기
    openBoninPayModal(groupIndex);
  } else {
    // 체크박스 비활성화 시 값 초기화
    settings.boninPay = '';
  }
  checkUnsavedChanges();
};

// 가족요양 수가금액 계산
const getFamilySugaMoney = (groupIndex: number): number => {
  const group = groupedMemberData.value[groupIndex];
  if (!group) return 0;
  
  let familySugaMoney = 0;
  group.patients.forEach((patient) => {
    // 가족요양인 경우만 수가금액 합산
    if (patient.sugaName && patient.sugaName.includes('가족')) {
      familySugaMoney += patient.totalSugaMoney || 0;
    }
  });
  
  return familySugaMoney;
};

// 본인부담금공제 모달 열기
const openBoninPayModal = (groupIndex: number) => {
  const group = groupedMemberData.value[groupIndex];
  if (!group) return;
  
  const familySugaMoney = getFamilySugaMoney(groupIndex);
  
  currentBoninPayGroupIndex.value = groupIndex;
  currentBoninPayCaregiver.value = {
    name: group.name,
    familySugaMoney: familySugaMoney
  };
  selectedBoninRate.value = null;
  showBoninPayModal.value = true;
};

// 본인부담금 계산 (원단위 절삭)
const calculateBoninAmount = (rate: number): number => {
  if (!currentBoninPayCaregiver.value) return 0;
  const amount = currentBoninPayCaregiver.value.familySugaMoney * rate / 100;
  return Math.floor(amount / 10) * 10; // 원단위 절삭
};

// 본인부담금 비율 선택
const selectBoninRate = (rate: number) => {
  selectedBoninRate.value = rate;
};

// 본인부담금 비율 적용
const applyBoninPayRate = () => {
  if (currentBoninPayGroupIndex.value === null || selectedBoninRate.value === null) return;
  
  const settings = settingsData.value[currentBoninPayGroupIndex.value];
  
  if (settings) {
    if (selectedBoninRate.value === 0) {
      // 0% 선택 시 공제 안 함
      settings.applyBoninPay = false;
      settings.boninPay = '';
    } else {
      const amount = calculateBoninAmount(selectedBoninRate.value);
      settings.applyBoninPay = true;
      settings.boninPay = new Intl.NumberFormat('ko-KR').format(amount);
    }
  }
  
  showBoninPayModal.value = false;
  checkUnsavedChanges();
};

// 기타공제 입력 이벤트
const onEmpEtcInput = (event: Event, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  let value = input.value;
  value = value.replace(/[^\d]/g, '');
  if (value.length > 9) {
    value = value.slice(0, 9);
  }
  input.value = value;
  settingsData.value[groupIndex].empEtc = value;
  checkUnsavedChanges();
};

const onEmpEtcFocus = (event: FocusEvent, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/,/g, '');
  // 값이 0이면 빈 문자열로 표시
  input.value = numericValue === '0' ? '' : numericValue;
};

const onEmpEtcBlur = (event: FocusEvent, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/[^\d]/g, '');
  
  if (numericValue) {
    const formattedValue = new Intl.NumberFormat('ko-KR').format(parseInt(numericValue));
    input.value = formattedValue;
    settingsData.value[groupIndex].empEtc = formattedValue;
  } else {
    input.value = '';
    settingsData.value[groupIndex].empEtc = '';
  }
  checkUnsavedChanges();
};

// 기타공제 체크박스 토글
const onEmpEtcToggle = (groupIndex: number) => {
  const settings = settingsData.value[groupIndex];
  if (!settings) return;
  
  if (settings.applyEmpEtc) {
    settings.empEtc = '0';
  } else {
    settings.empEtc = '';
  }
  checkUnsavedChanges();
};

// 고용보험 모달 열기 (체크박스 클릭 시)
const openEmploymentModal = (groupIndex: number) => {
  const settings = settingsData.value[groupIndex];
  const group = groupedMemberData.value[groupIndex];
  if (!settings || !group) return;
  
  const age = calculateAge(group.birth, props.yearMonth || '');
  
  currentEmploymentGroupIndex.value = groupIndex;
  currentEmploymentCaregiver.value = {
    name: group.name,
    age: age
  };
  
  // 현재 설정에 따라 옵션 초기화
  if (settings.applyEmploymentInsurance && settings.applyUnemployment) {
    selectedEmploymentOption.value = 'both';
  } else if (!settings.applyEmploymentInsurance && settings.applyUnemployment) {
    selectedEmploymentOption.value = 'unemployment-only';
  } else if (settings.applyEmploymentInsurance && !settings.applyUnemployment) {
    selectedEmploymentOption.value = 'both';
  } else {
    selectedEmploymentOption.value = 'none';
  }
  
  showEmploymentModal.value = true;
};

// 고용보험 모달 취소 (상태 변경 없이 그냥 닫기)
const cancelEmploymentModal = () => {
  showEmploymentModal.value = false;
  currentEmploymentGroupIndex.value = null;
  currentEmploymentCaregiver.value = null;
  selectedEmploymentOption.value = null;
};

// 고용보험 적용 확인
const applyEmploymentInsurance = () => {
  if (currentEmploymentGroupIndex.value === null || !selectedEmploymentOption.value) return;
  
  const settings = settingsData.value[currentEmploymentGroupIndex.value];
  if (!settings) return;
  
  switch (selectedEmploymentOption.value) {
    case 'both':
      // 고용보험 + 실업급여 모두 적용
      settings.applyEmploymentInsurance = true;
      settings.applyUnemployment = true;
      break;
    case 'unemployment-only':
      // 실업급여만 적용 (고용보험 미적용)
      settings.applyEmploymentInsurance = false;
      settings.applyUnemployment = true;
      break;
    case 'none':
      // 모두 미적용
      settings.applyEmploymentInsurance = false;
      settings.applyUnemployment = false;
      break;
  }
  
  showEmploymentModal.value = false;
  currentEmploymentGroupIndex.value = null;
  currentEmploymentCaregiver.value = null;
  selectedEmploymentOption.value = null;
  checkUnsavedChanges();
};

// 평균 시급 계산 (가중평균)
const getAverageTimePay = (group: GroupedMember) => {
  const groupIndex = groupedMemberData.value.indexOf(group);
  const settings = settingsData.value[groupIndex];
  
  if (!settings || !settings.timePays) return '-';
  
  let totalWeightedPay = 0;
  let totalHours = 0;
  
  group.patients.forEach((patient) => {
    const sugaKey = getSugaKey(patient);
    const timePay = settings.timePays[patient.patientCode]?.[sugaKey];
    if (timePay) {
      const numericPay = parseFloat(String(timePay).replace(/[^\d]/g, '')) || 0;
      const sugaHours = (patient.serviceDays || 0) * (patient.sugaHours || 0);
      
      if (numericPay > 0 && sugaHours > 0) {
        totalWeightedPay += numericPay * sugaHours;
        totalHours += sugaHours;
      }
    }
  });
  
  if (totalHours === 0) return '-';
  
  const average = Math.round(totalWeightedPay / totalHours);
  return new Intl.NumberFormat('ko-KR').format(average) + '원';
};

// 월급여 계산 (요보웨이지 로직 참고)
const calculateMonthlyPay = (group: GroupedMember) => {
  const groupIndex = groupedMemberData.value.indexOf(group);
  const settings = settingsData.value[groupIndex];
  
  if (!settings || !settings.timePays) return 0;
  
  let totalMonthlyPay = 0;
  
  group.patients.forEach((patient) => {
    const sugaKey = getSugaKey(patient);
    const timePay = settings.timePays[patient.patientCode]?.[sugaKey];
    if (timePay) {
      const numericPay = parseFloat(String(timePay).replace(/[^\d]/g, '')) || 0;
      const serviceDays = patient.serviceDays || 0;
      
      // 수동 조정된 시간이 있으면 사용, 없으면 휴게시간 적용된 시간 사용
      let sugaHours: number;
      if (settings.customSugaHours?.[patient.patientCode]?.[sugaKey] !== undefined) {
        sugaHours = settings.customSugaHours[patient.patientCode][sugaKey];
      } else {
        sugaHours = getPatientSugaHours(patient);  // 270분초과는 휴게시간 적용
      }
      const totalHours = serviceDays * sugaHours;
      
      totalMonthlyPay += numericPay * totalHours;
    }
  });
  
  return totalMonthlyPay;
};

// 기관 수익 계산 (총수가 - (월급여 + 기관공제))
// 월급여 = 총급여 = 기본급 + 수당
const calculateCenterProfit = (group: GroupedMember, groupIndex: number) => {
  const payroll = payrollDetails.value[groupIndex];
  if (!payroll) return 0;
  
  const totalSugaMoney = group.totalSugaMoney || 0;
  const monthlyPay = payroll.totalPay || 0; // 수가시간 × 시급 + 모든 수당
  const centerDeduction = payroll.totalCenterDeduction || 0;
  
  return totalSugaMoney - monthlyPay - centerDeduction;
};

// 금액 포맷팅
const formatMoney = (amount: number) => {
  if (!amount) return '0';
  return new Intl.NumberFormat('ko-KR').format(amount);
};

// 만 나이 계산
const calculateAge = (birthDateStr: string, baseYm: string) => {
  if (!birthDateStr || !baseYm) return 0;

  const birthDate = new Date(birthDateStr);
  const baseYear = parseInt(baseYm.substring(0, 4));
  const baseMonth = parseInt(baseYm.substring(4, 6));

  // 기준일 설정 (해당 월의 마지막 날)
  const baseDate = new Date(baseYear, baseMonth, 0);

  // 만 나이 계산
  let age = baseDate.getFullYear() - birthDate.getFullYear();

  // 생일이 지나지 않았으면 1살 빼기
  if (
    baseDate.getMonth() < birthDate.getMonth() ||
    (baseDate.getMonth() === birthDate.getMonth() && baseDate.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
};

// 근속년수 계산
const calculateWorkPeriod = (startDateStr: string, baseYm: string) => {
  if (!startDateStr || !baseYm) return { years: 0, months: 0 };

  const workStartDate = new Date(startDateStr);
  const baseYear = parseInt(baseYm.substring(0, 4));
  const baseMonth = parseInt(baseYm.substring(4, 6));
  const baseDate = new Date(baseYear, baseMonth - 1, 1);

  let years = baseDate.getFullYear() - workStartDate.getFullYear();
  let months = baseDate.getMonth() - workStartDate.getMonth();

  if (months < 0) {
    years--;
    months += 12;
  }

  return { years, months };
};

// 연차 일수 테이블 (TabYoboWage 로직 동일)
const vacationYearOptions = [
  { value: 1, label: '1년차', days: 11 },
  { value: 2, label: '2년차', days: 15 },
  { value: 3, label: '3년차', days: 15 },
  { value: 4, label: '4년차', days: 16 },
  { value: 5, label: '5년차', days: 16 },
  { value: 6, label: '6년차', days: 17 },
  { value: 7, label: '7년차', days: 17 },
  { value: 8, label: '8년차', days: 18 },
  { value: 9, label: '9년차', days: 18 },
  { value: 10, label: '10년차', days: 19 },
  { value: 11, label: '11년차', days: 19 },
  { value: 12, label: '12년차', days: 20 },
  { value: 13, label: '13년차', days: 20 },
  { value: 14, label: '14년차', days: 21 },
  { value: 15, label: '15년차', days: 21 },
  { value: 16, label: '16년차', days: 22 },
  { value: 17, label: '17년차', days: 22 },
  { value: 18, label: '18년차', days: 23 },
  { value: 19, label: '19년차', days: 23 },
  { value: 20, label: '20년차', days: 24 },
  { value: 21, label: '21년차이상', days: 25 },
];

// 근속연수에 따른 연차 일수 계산 함수 (TabYoboWage 로직 동일)
// 12개월 미만 -> 1년차, 12개월 이상 -> 2년차, 24개월 이상 -> 3년차
const getAnnualLeaveDays = (startDateStr: string) => {
  if (!startDateStr || !props.yearMonth) return 11; // 기본값: 1년차(11일)
  
  const period = calculateWorkPeriod(startDateStr, props.yearMonth);
  
  // 근속기간을 총 개월수로 변환
  const totalMonths = period.years * 12 + period.months;
  
  // 12개월 미만이면 1년차(11일)로 취급
  if (totalMonths < 12) {
    return 11;
  }
  
  // 년차 계산: 12개월 이상 -> 2년차, 24개월 이상 -> 3년차...
  const yearLevel = Math.floor(totalMonths / 12) + 1;
  
  // 21년차 이상은 25일
  if (yearLevel >= 21) {
    return 25;
  }
  
  // 년차에 따른 연차 일수 찾기
  const option = vacationYearOptions.find(opt => opt.value === yearLevel);
  
  return option ? option.days : 11; // fallback: 1년차(11일)
};

// 총 수가시간 계산 (서비스횟수 × 수가시간)
// 270분초과 수가는 30분 휴게시간 적용 (수동 조정되지 않은 경우)
// 수동 조정된 시간이 있으면 해당 값 사용
const getTotalSugaHours = (group: GroupedMember, groupIndex?: number) => {
  let totalHours = 0;
  group.patients.forEach((patient) => {
    let sugaHours: number;
    
    if (groupIndex !== undefined) {
      const settings = settingsData.value[groupIndex];
      const sugaKey = getSugaKey(patient);
      
      // 수동 조정된 시간이 있으면 그 값 사용
      if (settings?.customSugaHours?.[patient.patientCode]?.[sugaKey] !== undefined) {
        sugaHours = settings.customSugaHours[patient.patientCode][sugaKey];
      } else {
        // 수동 조정이 없으면 휴게시간 적용된 시간 사용
        sugaHours = getPatientSugaHours(patient);
      }
    } else {
      // groupIndex가 없으면 휴게시간 적용된 시간 사용
      sugaHours = getPatientSugaHours(patient);
    }
    
    totalHours += (patient.serviceDays || 0) * sugaHours;
  });
  return totalHours;
};

// 총 서비스 횟수 계산
const getTotalServiceDays = (group: GroupedMember) => {
  let totalDays = 0;
  group.patients.forEach((patient) => {
    totalDays += patient.serviceDays || 0;
  });
  return totalDays;
};

// 실제 근무일수 계산 (주차별 데이터에서 고유한 서비스일 개수)
const getActualWorkDays = (groupIndex: number) => {
  const weeklyData = combinedWeeklyServices.value[groupIndex];
  if (!weeklyData || weeklyData.length === 0) return 0;
  
  const uniqueDates = new Set<string>();
  weeklyData.forEach((weekGroup: any) => {
    weekGroup.daily_items?.forEach((daily: any) => {
      if (daily.service_date) {
        uniqueDates.add(daily.service_date);
      }
    });
  });
  
  return uniqueDates.size;
};

// 270분초과 수가인지 확인
const is270MinutesOverSuga = (patient: { sugaName?: string; sugaMinutesKey?: number | null }) => {
  return patient.sugaMinutesKey != null || (patient.sugaName && patient.sugaName.includes('270분초과'));
};

// 환자별 수가시간 가져오기 (270분초과 수가는 30분 휴게시간 적용)
// 270분초과 수가는 백엔드에서 실제 근무시간을 30분 단위로 끊어서 계산한 값 사용
// 단, 8시간 이상 시에는 휴게시간 차감 없음
const getPatientSugaHours = (patient: { sugaName?: string; sugaHours?: number; sugaMinutesKey?: number | null }) => {
  const baseHours = patient.sugaHours || 0;
  
  // 270분초과 수가인 경우 30분(0.5시간) 휴게시간 차감
  // 단, 8시간 이상 시에는 휴게시간 차감하지 않음
  if (is270MinutesOverSuga(patient)) {
    if (baseHours >= 8) {
      return baseHours;
    }
    return Math.max(0, baseHours - 0.5);
  }
  
  return baseHours;
};

// 수동 조정된 수가시간이 있으면 그 값을, 없으면 휴게시간 적용된 수가시간 반환
const getEffectiveSugaHours = (groupIndex: number, patient: PatientInfo) => {
  const settings = settingsData.value[groupIndex];
  const sugaKey = getSugaKey(patient);
  
  // 수동 조정된 값이 있으면 그대로 사용 (이미 사용자가 조정한 값)
  if (settings?.customSugaHours?.[patient.patientCode]?.[sugaKey] !== undefined) {
    return settings.customSugaHours[patient.patientCode][sugaKey];
  }
  
  // 270분초과 수가인 경우 30분 휴게시간 적용
  return getPatientSugaHours(patient);
};

// 수가시간이 수동으로 변경되었는지 확인
const isSugaHoursManuallyChanged = (groupIndex: number, patientCode: string, sugaKey: string) => {
  const settings = settingsData.value[groupIndex];
  return settings?.customSugaHours?.[patientCode]?.[sugaKey] !== undefined;
};

// 수가시간 조정 모달 열기
const openSugaHoursModal = (groupIndex: number, patient: PatientInfo) => {
  if (isConfirmed.value) return;
  
  const sugaKey = getSugaKey(patient);
  const effectiveHours = getEffectiveSugaHours(groupIndex, patient);
  
  // 원래 시간도 휴게시간이 적용된 값 (270분초과의 경우 -0.5시간)
  const originalHoursWithBreak = getPatientSugaHours(patient);
  
  currentSugaHoursGroupIndex.value = groupIndex;
  currentSugaHoursPatient.value = {
    patientCode: patient.patientCode,
    patientName: patient.patientName,
    sugaKey: sugaKey,
    sugaName: getDisplaySugaName(patient),
    originalHours: originalHoursWithBreak  // 휴게시간 적용된 원래 시간
  };
  selectedSugaHours.value = effectiveHours;
  showSugaHoursModal.value = true;
};

// 수가시간 조정 (+/- 0.5시간)
const adjustSugaHours = (delta: number) => {
  const newValue = selectedSugaHours.value + delta;
  if (newValue >= 0.5 && newValue <= 24) {
    selectedSugaHours.value = Math.round(newValue * 2) / 2; // 0.5 단위로 반올림
  }
};

// 수가시간 변경 적용
const applySugaHoursChange = () => {
  if (currentSugaHoursGroupIndex.value === null || !currentSugaHoursPatient.value) return;
  
  const groupIndex = currentSugaHoursGroupIndex.value;
  const { patientCode, sugaKey, originalHours } = currentSugaHoursPatient.value;
  const settings = settingsData.value[groupIndex];
  
  if (!settings) return;
  
  // customSugaHours 초기화
  if (!settings.customSugaHours) {
    settings.customSugaHours = {};
  }
  
  // 원래 값과 다르면 수동 변경으로 저장, 같으면 삭제 (원래 값 복원)
  if (selectedSugaHours.value !== originalHours) {
    // 새 값 설정 - 불변성 유지
    settings.customSugaHours = {
      ...settings.customSugaHours,
      [patientCode]: {
        ...(settings.customSugaHours[patientCode] || {}),
        [sugaKey]: selectedSugaHours.value
      }
    };
    settings.sugaHoursChanged = true;
  } else {
    // Vue 반응성을 위해 새 객체로 재할당
    if (settings.customSugaHours[patientCode]) {
      const { [sugaKey]: removed, ...restSugaKeys } = settings.customSugaHours[patientCode];
      
      if (Object.keys(restSugaKeys).length === 0) {
        // 해당 환자의 모든 수가시간이 원래 값이면 patientCode도 제거
        const { [patientCode]: removedPatient, ...restPatients } = settings.customSugaHours;
        settings.customSugaHours = restPatients;
      } else {
        settings.customSugaHours = {
          ...settings.customSugaHours,
          [patientCode]: restSugaKeys
        };
      }
      
      // 모든 수동 변경이 복원되었으면 sugaHoursChanged를 false로
      if (Object.keys(settings.customSugaHours).length === 0) {
        settings.sugaHoursChanged = false;
      }
    }
  }
  
  checkUnsavedChanges();
  showSugaHoursModal.value = false;
  
  // 급여 재계산
  const group = groupedMemberData.value[groupIndex];
  if (group) {
    payrollDetails.value[groupIndex] = calculatePayrollDetails(group, groupIndex);
  }
};

// 드롭다운 키 생성
const getSugaHoursDropdownKey = (groupIndex: number, patientCode: string, sugaKey: string) => {
  return `${groupIndex}_${patientCode}_${sugaKey}`;
};

// 드롭다운 열림 여부 확인
const isSugaHoursDropdownOpen = (groupIndex: number, patientCode: string, sugaKey: string) => {
  return openSugaHoursDropdown.value === getSugaHoursDropdownKey(groupIndex, patientCode, sugaKey);
};

// 드롭다운 토글
const toggleSugaHoursDropdown = (groupIndex: number, patient: PatientInfo) => {
  if (isConfirmed.value) return;
  
  const sugaKey = getSugaKey(patient);
  const key = getSugaHoursDropdownKey(groupIndex, patient.patientCode, sugaKey);
  
  if (openSugaHoursDropdown.value === key) {
    openSugaHoursDropdown.value = null;
  } else {
    openSugaHoursDropdown.value = key;
  }
};

// 인라인에서 수가시간 조정 (+/- 0.5시간)
const adjustSugaHoursInline = (groupIndex: number, patient: PatientInfo, delta: number) => {
  const sugaKey = getSugaKey(patient);
  const currentHours = getEffectiveSugaHours(groupIndex, patient);
  const newValue = Math.round((currentHours + delta) * 2) / 2; // 0.5 단위로 반올림
  
  if (newValue < 0.5 || newValue > 24) return;
  
  const settings = settingsData.value[groupIndex];
  if (!settings) return;
  
  // customSugaHours 초기화 - 새 객체로 할당하여 반응성 보장
  if (!settings.customSugaHours) {
    settings.customSugaHours = {};
  }
  
  // 원래 값(휴게시간 적용된 값)과 비교
  const originalHours = getPatientSugaHours(patient);
  
  if (newValue !== originalHours) {
    // 새 값 설정 - 불변성 유지
    settings.customSugaHours = {
      ...settings.customSugaHours,
      [patient.patientCode]: {
        ...(settings.customSugaHours[patient.patientCode] || {}),
        [sugaKey]: newValue
      }
    };
    settings.sugaHoursChanged = true;
  } else {
    // 원래 값으로 돌아가면 수동 변경 삭제 - Vue 반응성을 위해 새 객체로 재할당
    if (settings.customSugaHours[patient.patientCode]) {
      const { [sugaKey]: removed, ...restSugaKeys } = settings.customSugaHours[patient.patientCode];
      
      if (Object.keys(restSugaKeys).length === 0) {
        // 해당 patientCode의 모든 수가시간이 삭제되면 patientCode도 제거
        const { [patient.patientCode]: removedPatient, ...restPatients } = settings.customSugaHours;
        settings.customSugaHours = restPatients;
      } else {
        // 아직 다른 수가시간이 남아있으면 해당 sugaKey만 제거
        settings.customSugaHours = {
          ...settings.customSugaHours,
          [patient.patientCode]: restSugaKeys
        };
      }
      
      if (Object.keys(settings.customSugaHours).length === 0) {
        settings.sugaHoursChanged = false;
      }
    }
  }
  
  checkUnsavedChanges();
  
  // 급여 재계산
  const group = groupedMemberData.value[groupIndex];
  if (group) {
    payrollDetails.value[groupIndex] = calculatePayrollDetails(group, groupIndex);
  }
};

// 자동계산으로 변경 (수동 조정 취소)
const resetToAutoCalculation = async (groupIndex: number, patient: PatientInfo) => {
  const sugaKey = getSugaKey(patient);
  const settings = settingsData.value[groupIndex];
  
  if (!settings || !settings.customSugaHours) {
    return;
  }
  
  // 수동 변경 삭제 - Vue 반응성을 위해 새 객체로 재할당
  if (settings.customSugaHours[patient.patientCode]) {
    // 해당 sugaKey 제거한 새 객체 생성
    const { [sugaKey]: removed, ...restSugaKeys } = settings.customSugaHours[patient.patientCode];
    
    if (Object.keys(restSugaKeys).length === 0) {
      // 해당 patientCode의 모든 수가시간이 삭제되면 patientCode도 제거
      const { [patient.patientCode]: removedPatient, ...restPatients } = settings.customSugaHours;
      settings.customSugaHours = restPatients;
    } else {
      // 아직 다른 수가시간이 남아있으면 해당 sugaKey만 제거
      settings.customSugaHours = {
        ...settings.customSugaHours,
        [patient.patientCode]: restSugaKeys
      };
    }
    
    // 모든 수동 변경이 복원되었으면 sugaHoursChanged를 false로
    if (Object.keys(settings.customSugaHours).length === 0) {
      settings.sugaHoursChanged = false;
    }
  }
  
  checkUnsavedChanges();
  
  // 드롭다운 닫기
  openSugaHoursDropdown.value = null;
  
  // 급여 재계산
  const group = groupedMemberData.value[groupIndex];
  if (group) {
    payrollDetails.value[groupIndex] = calculatePayrollDetails(group, groupIndex);
  }
  
  store.commit('showSnackbar', { 
    text: '자동계산 시간으로 변경되었습니다.', 
    color: 'success' 
  });
};

// 수가시간 포맷팅
const formatSugaHours = (hours: number) => {
  if (!hours) return '0시간';
  if (hours % 1 === 0) {
    return `${hours}시간`;
  } else {
    return `${hours.toFixed(1)}시간`;
  }
};

// 사원 특징 추출 (일반 정보와 제한사항 분리)
const getEmployeeFeatures = (group: GroupedMember, groupIndex: number) => {
  const info: Array<{ text: string; type: string; icon: string; iconColor: string }> = [];
  const restrictions: Array<{ 
    text: string; 
    type: string; 
    icon: string; 
    iconColor: string;
    expandable?: boolean;
    expanded?: boolean;
    details?: Array<{ text: string; icon: string; iconColor: string }>;
  }> = [];
  const settings = settingsData.value[groupIndex];
  
  if (!props.yearMonth || !settings) return { info, restrictions };

  const age = calculateAge(group.birth, props.yearMonth);
  const totalSugaHours = getTotalSugaHours(group, groupIndex);
  const totalServiceDays = getTotalServiceDays(group);
  const workPeriod = calculateWorkPeriod(group.workStartDate, props.yearMonth);
  
  // === 왼쪽: 일반 정보 ===
  
  // 1. 나이
  info.push({
    text: `만 ${age}세`,
    type: 'info',
    icon: 'mdi-cake-variant',
    iconColor: '#3b82f6'
  });

  // 2. 근속 기간
  const periodText = workPeriod.years > 0 
    ? `${workPeriod.years}년 ${workPeriod.months}개월`
    : `${workPeriod.months}개월`;
  
  info.push({
    text: `근속 ${periodText}`,
    type: 'info',
    icon: 'mdi-calendar-clock',
    iconColor: '#3b82f6'
  });

  // 3. 서비스 횟수
  info.push({
    text: `서비스 횟수 ${totalServiceDays}회`,
    type: 'info',
    icon: 'mdi-counter',
    iconColor: '#3b82f6'
  });

  // 4. 서비스 시간 (수가시간)
  info.push({
    text: `서비스 시간 ${formatSugaHours(totalSugaHours)}`,
    type: totalSugaHours >= 60 ? 'success' : 'warning',
    icon: 'mdi-clock-outline',
    iconColor: totalSugaHours >= 60 ? '#10b981' : '#f59e0b'
  });

  // 5. 장기근속수당
  const longServicePay = settings.longServicePay ? String(settings.longServicePay).replace(/[^\d]/g, '') : '';
  if (longServicePay && parseInt(longServicePay) > 0) {
    info.push({
      text: `장기근속수당 ${new Intl.NumberFormat('ko-KR').format(parseInt(longServicePay))}원`,
      type: 'info',
      icon: 'mdi-gift',
      iconColor: '#8b5cf6'
    });
  }

  // 6. 가족 케어 여부
  const isFamilyCaregiver = group.patients.some(p => 
    p.sugaName && p.sugaName.includes('가족')
  );
  
  if (isFamilyCaregiver) {
    info.push({
      text: '가족 케어 포함',
      type: 'info',
      icon: 'mdi-home-heart',
      iconColor: '#8b5cf6'
    });
  }

  // === 오른쪽: 제한사항 ===

  // 60시간 미만일 때의 제약사항
  if (totalSugaHours < 60) {
    const isExpanded = expandedRestrictions.value[groupIndex]?.has(0) || false;
    const year = parseInt(props.yearMonth?.substring(0, 4) || '2026');
    
    // 2025년 이하에서만 근로자의날 수당 지급 불가 메시지 표시
    const details = [
      { text: '퇴직금 적립 불가', icon: 'mdi-close-circle', iconColor: '#ef4444' },
      { text: '휴일수당 지급 불가', icon: 'mdi-close-circle', iconColor: '#ef4444' },
      { text: '연장수당 지급 불가', icon: 'mdi-close-circle', iconColor: '#ef4444' },
      ...(year <= 2025 ? [{ text: '근로자의날 수당 지급 불가', icon: 'mdi-close-circle', iconColor: '#ef4444' }] : []),
      { text: '지원공제 중 퇴직금 부분(장기근속수당÷12) 제외', icon: 'mdi-close-circle', iconColor: '#ef4444' }
    ];
    
    restrictions.push({
      text: `⚠️ 월 수가시간 60시간 미만 (${totalSugaHours.toFixed(1)}시간)`,
      type: 'warning-header',
      icon: 'mdi-alert-circle',
      iconColor: '#f59e0b',
      expandable: true,
      expanded: isExpanded,
      details
    });
  }

  // 나이로 인한 제약
  let ageRestrictionIndex = restrictions.length;
  if (age >= 60) {
    restrictions.push({
      text: '⚠️ 만 60세 이상: 국민연금 미적용',
      type: 'warning',
      icon: 'mdi-shield-off',
      iconColor: '#f59e0b'
    });
  }

  if (age >= 65) {
    restrictions.push({
      text: '⚠️ 만 65세 이상: 고용보험 미적용',
      type: 'warning',
      icon: 'mdi-briefcase-off',
      iconColor: '#f59e0b'
    });
  }

  // 대표자인 경우 제약
  if (group.isBoss) {
    restrictions.push({
      text: '⚠️ 대표자: 고용보험, 산재보험, 퇴직금 미적용',
      type: 'warning',
      icon: 'mdi-account-star',
      iconColor: '#10b981'
    });
  }

  // 가족요양 전용 + 60시간 미만인 경우 제약
  const isFamilyOnly = isAllFamilyCaregiver(group);
  if (isFamilyOnly && totalSugaHours < 60) {
    restrictions.push({
      text: '⚠️ 가족요양 60시간 미만: 국민연금, 건강보험, 식대 미적용',
      type: 'warning',
      icon: 'mdi-home-heart',
      iconColor: '#8b5cf6'
    });
  }

  return { info, restrictions };
};

// 가족 케어 전용 여부 확인
const isAllFamilyCaregiver = (group: GroupedMember): boolean => {
  if (!group || !group.patients || group.patients.length === 0) {
    return false;
  }
  
  // 모든 수가명에 '가족'이 포함되어 있는지 확인
  return group.patients.every((patient) => {
    const sugaName = patient.sugaName || '';
    return sugaName.includes('가족');
  });
};

// 원단위 절삭 함수 (예: 51131 -> 51130)
const truncateToTen = (value: number) => Math.floor(value / 10) * 10;

// 소득세 계산 함수 (간이세액표 기반)
const calculateIncomeTax = (taxableIncome: number) => {
  if (!sojiData.value || sojiData.value.length === 0) {
    return 0;
  }

  // 소득세 데이터에서 해당 구간 찾기
  for (let i = 0; i < sojiData.value.length; i++) {
    const obj = sojiData.value[i];
    const minIncome = obj.income1 * 1000;
    const maxIncome = obj.income2 * 1000;

    if (taxableIncome >= minIncome && taxableIncome < maxIncome) {
      const result = Math.round(obj.depe_1);
      return result;
    }
  }

  return 0;
};

// 급여 상세 계산 (TabYoboWage 로직 완전 반영)
const calculatePayrollDetails = (group: GroupedMember, groupIndex: number) => {
  const settings = settingsData.value[groupIndex];
  if (!settings) return null;

  const totalSugaHours = getTotalSugaHours(group, groupIndex);  // 수동 조정된 시간 반영
  const serviceDays = getTotalServiceDays(group);

  // 평균 시급 계산 (가중평균) - 휴게시간 및 수동 조정된 시간 반영
  let totalWeightedPay = 0;
  let totalHours = 0;
  
  group.patients.forEach((patient) => {
    const sugaKey = getSugaKey(patient);
    const timePay = settings.timePays[patient.patientCode]?.[sugaKey];
    if (timePay) {
      const numericPay = parseFloat(String(timePay).replace(/[^\d]/g, '')) || 0;
      
      // 수동 조정된 수가시간이 있으면 사용, 없으면 휴게시간 적용된 시간 사용
      let patientSugaHours: number;
      if (settings.customSugaHours?.[patient.patientCode]?.[sugaKey] !== undefined) {
        patientSugaHours = settings.customSugaHours[patient.patientCode][sugaKey];
      } else {
        patientSugaHours = getPatientSugaHours(patient);  // 270분초과는 휴게시간 적용
      }
      const sugaHours = (patient.serviceDays || 0) * patientSugaHours;
      
      if (numericPay > 0 && sugaHours > 0) {
        totalWeightedPay += numericPay * sugaHours;
        totalHours += sugaHours;
      }
    }
  });

  const avgTimePay = totalHours > 0 ? Math.round(totalWeightedPay / totalHours) : 0;

  // 가족 케어 전용 여부 확인
  const isFamilyOnlyCaregiver = isAllFamilyCaregiver(group);
  
  // 월급여 (계산된 값: 시급 × 수가시간)
  const totalMonthlyPay = calculateMonthlyPay(group);
  
  // 장기근속수당
  const longServicePay = settings.longServicePay ? 
    parseFloat(String(settings.longServicePay).replace(/[^\d]/g, '')) || 0 : 0;
  
  // 기타수당
  const etcAllowance = settings.etcAllowance ? 
    parseFloat(String(settings.etcAllowance).replace(/[^\d]/g, '')) || 0 : 0;
  
  // 중증수급자가산 (자동 계산)
  const severeCareAddition = getSevereCareAdditionTotal(groupIndex);
  
  // 수당 계산 (60시간 이상일 때만)
  const canApplyAllowances = totalSugaHours >= 60;
  
  // 휴일수당: 실제 휴일(일요일+공휴일) 근무 시간 × 최저시급 × 0.5 (비과세)
  // 체크박스 상태가 제한사항보다 우선 (사용자가 체크하면 적용)
  const holidayAllowance = settings.applyHoliday ? 
    calculateHolidayAllowanceAmount(groupIndex) : 0;
  
  // 연장수당: 실제 연장 근무 시간(일일 8시간 초과) × 최저시급 × 0.5 (비과세)
  // 체크박스 상태가 제한사항보다 우선 (사용자가 체크하면 적용)
  const overtimeAllowance = settings.applyOvertime ? 
    calculateOvertimeAllowanceAmount(groupIndex) : 0;
  
  // 기본 급여 항목 계산
  let basicPay = 0;
  let holidayPay = 0;
  let vacationPay = 0;
  let actualMealAllowance = 0;
  
  if (isFamilyOnlyCaregiver) {
    // 가족 케어 전용: 월급여 전체가 기본급 (주휴수당, 연차수당, 식대 모두 포함)
    basicPay = totalMonthlyPay;
    holidayPay = 0;
    vacationPay = 0;
    actualMealAllowance = 0;
  } else {
    // 일반 요양: 최저시급 기반 계산
    const minWage = taxData.value?.tr_time_pay || 0;
    
    if (minWage > 0) {
      // 1. 기본급 = 최저시급 × 수가시간
      basicPay = minWage * totalSugaHours;
      
      // 2. 주휴수당 = 기본급 × 0.2
      holidayPay = Math.round(basicPay * 0.2);
      
      // 3. 연차수당 = 공식 계산 (고정값, 먼저 확보)
      // 실제 근무일수 사용 (서비스 횟수가 아닌 고유한 근무일 수)
      // fallback: 실제 근무일수가 0이면 서비스 횟수 사용
      const annualLeaveDays = getAnnualLeaveDays(group.workStartDate);
      const actualWorkDays = getActualWorkDays(groupIndex);
      const workDaysForCalc = actualWorkDays > 0 ? actualWorkDays : serviceDays;
      console.log(workDaysForCalc,'workDaysForCalc');
      vacationPay = workDaysForCalc > 0 
        ? Math.round((minWage * totalSugaHours / workDaysForCalc) * annualLeaveDays / 12) 
        : 0;
      
      // [60시간 미만] 시급 역산 및 급여 보정
      // 기본급+주휴수당+연차수당이 월급여(수급자별 시급×수가시간 합계)와 일치하도록 조정
      if (totalSugaHours < 60) {
        const basicPlusHoliday = basicPay + holidayPay;
        const currentTotal = basicPlusHoliday + vacationPay;
        
        if (currentTotal > totalMonthlyPay) {
          // 초과하는 경우: 연차수당 조정
          const adjustedVacationPay = Math.max(0, totalMonthlyPay - basicPlusHoliday);
          vacationPay = adjustedVacationPay;
        } else if (currentTotal < totalMonthlyPay) {
          // 미달하는 경우: 시급 역산하여 기본급/주휴수당/연차수당 모두 동일 시급으로 계산
          // 1. 연차수당까지 포함한 이론적 최대 시급 계산
          // 합계금액 = 시급 × 수가시간 × (1 + 0.2 + 연차일수/12/근무일수)
          const vacationRatio = workDaysForCalc > 0 ? (annualLeaveDays / 12) / workDaysForCalc : 0;
          const totalRatio = 1 + 0.2 + vacationRatio;
          const theoreticalMaxWage = totalMonthlyPay / (totalSugaHours * totalRatio);
          
          // 2. 10원 단위로 내림 (합계금액 미만 보장)
          const adjustedWage = Math.floor(theoreticalMaxWage / 10) * 10;
          
          // 3. 최저시급보다 높은 경우에만 역산 적용
          if (adjustedWage > minWage) {
            // 역산 시급으로 기본급, 주휴수당, 연차수당 모두 동일 시급으로 재계산
            basicPay = Math.round(adjustedWage * totalSugaHours);
            holidayPay = Math.round(basicPay * 0.2);
            vacationPay = workDaysForCalc > 0 
              ? Math.round((adjustedWage * totalSugaHours / workDaysForCalc) * annualLeaveDays / 12) 
              : 0;
          }
          
          // 4. 남은 차액을 연차수당에 추가
          const newTotal = basicPay + holidayPay + vacationPay;
          const difference = totalMonthlyPay - newTotal;
          if (difference > 0) {
            vacationPay += difference;
          }
        }
        
        // 60시간 미만이면 식대 없음
        actualMealAllowance = 0;
      } else {
        // [60시간 이상] 기존 로직: 남은 금액 계산 및 식대 처리
        // 4. 남은 금액 계산 (월급여 - 기본급 - 주휴수당 - 연차수당)
        const remainingAmount = Math.max(0, totalMonthlyPay - basicPay - holidayPay - vacationPay);
        
        // 5. 식대 계산 (남은 금액에서)
        if (settings.applyMeal && group.totalLunchTimeCount > 0) {
          // 요청 식대 계산 (일수 × 20,000원, 최대 200,000원)
          const requestedMealAllowance = Math.min(group.totalLunchTimeCount * 20000, 200000);
          
          // 실제 지급 가능한 금액 (둘 중 작은 값)
          const availableAmount = Math.min(requestedMealAllowance, remainingAmount);
          
          // 백원 단위로 내림
          const roundedAmount = Math.floor(availableAmount / 100) * 100;
          
          // 최소 5,000원 이상이면 식대 지급, 아니면 0원
          if (roundedAmount >= 5000) {
            actualMealAllowance = roundedAmount;
            // 식대 지급 후 남은 차액을 연차수당에 추가
            vacationPay += (remainingAmount - actualMealAllowance);
          } else {
            // 식대 기준치 미달 시, 남은 금액 전부를 연차수당에 추가
            actualMealAllowance = 0;
            vacationPay += remainingAmount;
          }
        } else {
          // 식대 미적용 시, 남은 금액 전부를 연차수당에 추가
          vacationPay += remainingAmount;
        }
      }
  } else {
    // taxData가 없으면 기존 방식대로 계산
    // 1. 기본급, 주휴수당 먼저 계산 (월급여 기준 비율)
    // fallback: 실제 근무일수가 0이면 서비스 횟수 사용
    const annualLeaveDays = getAnnualLeaveDays(group.workStartDate);
    const actualWorkDays = getActualWorkDays(groupIndex);
    const workDaysForCalc = actualWorkDays > 0 ? actualWorkDays : serviceDays;
    const vacationRatio = workDaysForCalc > 0 ? (annualLeaveDays / 12) / workDaysForCalc : 0;
    
    const totalRatio = 1 + 0.2 + vacationRatio;
    basicPay = Math.round(totalMonthlyPay / totalRatio);
    holidayPay = Math.round(basicPay * 0.2);
    
    // 2. 연차수당 = 공식 계산 (고정값, 먼저 확보)
    vacationPay = Math.round(basicPay * vacationRatio);
    
    // [60시간 미만] 연차수당 초과 방지
    if (totalSugaHours < 60) {
      const basicPlusHoliday = basicPay + holidayPay;
      const currentTotal = basicPlusHoliday + vacationPay;
      
      if (currentTotal > totalMonthlyPay) {
        const adjustedVacationPay = Math.max(0, totalMonthlyPay - basicPlusHoliday);
        vacationPay = adjustedVacationPay;
      }
      
      actualMealAllowance = 0;
    } else {
      // [60시간 이상] 기존 로직
      // 3. 남은 금액 계산 (월급여 - 기본급 - 주휴수당 - 연차수당)
      const remainingAmount = Math.max(0, totalMonthlyPay - basicPay - holidayPay - vacationPay);
      
      // 4. 식대 계산 (남은 금액에서)
      if (settings.applyMeal && group.totalLunchTimeCount > 0) {
        // 요청 식대 계산 (일수 × 20,000원, 최대 200,000원)
        const requestedMealAllowance = Math.min(group.totalLunchTimeCount * 20000, 200000);
        
        // 실제 지급 가능한 금액 (둘 중 작은 값)
        const availableAmount = Math.min(requestedMealAllowance, remainingAmount);
        
        // 백원 단위로 내림
        const roundedAmount = Math.floor(availableAmount / 100) * 100;
        
        // 최소 5,000원 이상이면 식대 지급, 아니면 0원
        if (roundedAmount >= 5000) {
          actualMealAllowance = roundedAmount;
          // 식대 지급 후 남은 차액을 연차수당에 추가
          vacationPay += (remainingAmount - actualMealAllowance);
        } else {
          // 식대 기준치 미달 시, 남은 금액 전부를 연차수당에 추가
          actualMealAllowance = 0;
          vacationPay += remainingAmount;
        }
      } else {
        // 식대 미적용 시, 남은 금액 전부를 연차수당에 추가
        vacationPay += remainingAmount;
      }
    }
  }
  }
  
  // 기본급 총합 계산 (TabYoboWage 로직 동일)
  let basicTotal;
  if (isFamilyOnlyCaregiver) {
    // 가족 케어 전용: 월급여 전체가 기본급 (주휴수당, 연차수당, 식대 모두 포함)
    basicTotal = totalMonthlyPay;
  } else {
    // 일반: 기본급 + 주휴수당 + 연차수당 + 식대
    basicTotal = basicPay + holidayPay + vacationPay + actualMealAllowance;
  }
  
  // 급여 소계 (비과세 조건 확인용) - basicTotal에 이미 식대 포함됨
  const salarySubtotal = basicTotal;
  
  // 총 급여 계산 (TabYoboWage 로직 동일)
  const totalPay = basicTotal + longServicePay + etcAllowance + severeCareAddition + holidayAllowance + overtimeAllowance;
  
  // 비과세 처리 조건: 수가시간 60시간 이상 && 급여 소계 210만원 이하
  const isSalaryUnder2_1M = salarySubtotal <= 2100000;
  
  // 휴일수당 + 연장수당 비과세 처리 (합계 20만원 한도)
  let nonTaxableHoliday = 0;
  let nonTaxableOvertime = 0;
  
  if (canApplyAllowances && isSalaryUnder2_1M) {
    const totalOvertimeHolidayAllowance = holidayAllowance + overtimeAllowance;
    
    if (totalOvertimeHolidayAllowance <= 200000) {
      // 합계가 20만원 이하면 전액 비과세
      nonTaxableHoliday = holidayAllowance;
      nonTaxableOvertime = overtimeAllowance;
    } else {
      // 합계가 20만원 초과면 비율로 배분
      const nonTaxableRatio = 200000 / totalOvertimeHolidayAllowance;
      nonTaxableHoliday = Math.round(holidayAllowance * nonTaxableRatio);
      nonTaxableOvertime = Math.round(overtimeAllowance * nonTaxableRatio);
      
      // 반올림으로 인한 오차 보정 (합계가 정확히 20만원이 되도록)
      const totalNonTaxable = nonTaxableHoliday + nonTaxableOvertime;
      if (totalNonTaxable !== 200000) {
        nonTaxableOvertime += (200000 - totalNonTaxable);
      }
    }
  }
  
  // 식대 비과세 한도 (20만원)
  const nonTaxableMeal = Math.min(actualMealAllowance, 200000);
  const taxableMeal = actualMealAllowance - nonTaxableMeal;
  
  // 과세 처리되는 휴일수당, 연장수당
  const taxableHoliday = holidayAllowance - nonTaxableHoliday;
  const taxableOvertime = overtimeAllowance - nonTaxableOvertime;
  
  // 과세/비과세 구분 (장기근속수당, 기타수당, 중증수급자가산은 과세)
  const taxableAmount = basicTotal + longServicePay + etcAllowance + severeCareAddition + taxableHoliday + taxableOvertime + taxableMeal - actualMealAllowance;
  const nonTaxableAmount = nonTaxableMeal + nonTaxableHoliday + nonTaxableOvertime;

  // 보험 요율 (taxData에서 동적으로 가져옴)
  const insuranceRates = {
    nationalPension: taxData.value?.tr_yeon || 0.045,
    healthInsurance: taxData.value?.tr_gun || 0.03545,
    longTermCare: taxData.value?.tr_jang || 0.1295,
    employmentInsurance: taxData.value?.tr_go || 0.009,
    unemployment: taxData.value?.tr_sil || 0.0025,
    industrialAccident: taxData.value?.tr_san || 0.0178
  };

  const age = calculateAge(group.birth, props.yearMonth || '');
  
  // 대표자 여부 확인 (대표자는 고용보험, 산재보험, 퇴직금 미적용)
  const isBoss = group.isBoss;
  
  // 국민연금 계산 (과세금액 × 국민연금 요율) - 나이와 상관없이 계산
  const suggestedNationalPension = truncateToTen(taxableAmount * insuranceRates.nationalPension);
  
  // 국민연금 금액 사용 (체크박스 활성화 + 입력값이 있으면 사용, 체크 해제 시 0)
  const yeonPayAmount = settings.applyNationalPension && settings.yeonPay ? 
    parseFloat(String(settings.yeonPay).replace(/[^\d]/g, '')) || 0 : 0;
  const canApplyPension = settings.applyNationalPension && yeonPayAmount > 0;
  // 고용보험 적용 여부 (체크박스 상태가 제한사항보다 우선)
  const canApplyEmployment = settings.applyEmploymentInsurance;

  // 근로자 공제 (원단위 절삭 적용)
  const employeeDeductions = {
    nationalPension: canApplyPension ? truncateToTen(yeonPayAmount) : 0, // 직접 입력한 금액도 원단위 절삭
    healthInsurance: settings.applyHealthInsurance ? truncateToTen(taxableAmount * insuranceRates.healthInsurance) : 0,
    longTermCare: 0,
    employmentInsurance: canApplyEmployment ? truncateToTen(taxableAmount * insuranceRates.employmentInsurance) : 0,
    incomeTax: truncateToTen(calculateIncomeTax(taxableAmount)), // 간이세액표 기반 + 원단위 절삭
    localTax: 0
  };
  
  employeeDeductions.longTermCare = truncateToTen(employeeDeductions.healthInsurance * insuranceRates.longTermCare);
  employeeDeductions.localTax = truncateToTen(employeeDeductions.incomeTax * 0.1);

  // 본인부담금공제 금액 가져오기
  const boninPayAmount = settings.applyBoninPay && settings.boninPay ? 
    parseFloat(String(settings.boninPay).replace(/[^\d]/g, '')) || 0 : 0;

  // 기타공제 금액 가져오기 (미리 계산)
  const empEtcAmountForCalc = settings.applyEmpEtc && settings.empEtc ? 
    parseFloat(String(settings.empEtc).replace(/[^\d]/g, '')) || 0 : 0;

  // 근로자 공제 합계 (본인부담금공제 + 기타공제 + 지원공제 포함) - 지원공제는 나중에 추가
  let totalEmployeeDeduction = 
    employeeDeductions.nationalPension +
    employeeDeductions.healthInsurance +
    employeeDeductions.longTermCare +
    employeeDeductions.employmentInsurance +
    employeeDeductions.incomeTax +
    employeeDeductions.localTax +
    boninPayAmount +
    empEtcAmountForCalc;

  // 실업급여 적용 여부 (65세 이상은 고용보험과 별도 관리)
  const canApplyUnemployment = settings.applyUnemployment;
  
  // 기관 공제 (체크박스 상태가 제한사항보다 우선, 원단위 절삭 적용)
  const centerDeductions = {
    nationalPension: employeeDeductions.nationalPension,
    healthInsurance: employeeDeductions.healthInsurance,
    longTermCare: employeeDeductions.longTermCare,
    employmentInsurance: employeeDeductions.employmentInsurance,
    unemployment: canApplyUnemployment ? truncateToTen(taxableAmount * insuranceRates.unemployment) : 0,
    industrialAccident: truncateToTen(taxableAmount * insuranceRates.industrialAccident),
    retirement: settings.applyRetirement ? truncateToTen(totalPay / 12) : 0
  };

  // 지원공제 계산 (장기근속수당+중증수급자가산에 대한 기관 공제 항목 합계)
  // 공식: (장기근속수당+중증수급자가산) × (국민연금율 + 건강보험율 + 장기요양보험율 + 고용보험율 + 실업급여율 + 산재보험율) + ((장기근속수당+중증수급자가산) ÷ 12)
  const supportDeductionBase = longServicePay + severeCareAddition;
  let supportDeduction = 0;
  
  // 지원공제 세부 항목 저장 (툴팁용)
  const supportDeductionDetails = {
    base: supportDeductionBase,
    longServicePay: longServicePay,
    severeCareAddition: severeCareAddition,
    nationalPension: 0,
    healthInsurance: 0,
    longTermCare: 0,
    employmentInsurance: 0,
    unemployment: 0,
    industrialAccident: 0,
    retirement: 0,
    rates: {
      nationalPension: insuranceRates.nationalPension,
      healthInsurance: insuranceRates.healthInsurance,
      longTermCare: insuranceRates.longTermCare,
      employmentInsurance: insuranceRates.employmentInsurance,
      unemployment: insuranceRates.unemployment,
      industrialAccident: insuranceRates.industrialAccident
    }
  };
  
  if (supportDeductionBase > 0) {
    // 국민연금 (근로자 부담) - 원단위 절삭 적용
    if (canApplyPension) {
      supportDeductionDetails.nationalPension = truncateToTen(supportDeductionBase * insuranceRates.nationalPension);
      supportDeduction += supportDeductionDetails.nationalPension;
    }

    // 건강보험 (근로자 부담) - 원단위 절삭 적용
    if (settings.applyHealthInsurance) {
      supportDeductionDetails.healthInsurance = truncateToTen(supportDeductionBase * insuranceRates.healthInsurance);
      supportDeduction += supportDeductionDetails.healthInsurance;
    }

    // 장기요양보험 (근로자 부담) - 건강보험의 12.95%, 원단위 절삭 적용
    if (settings.applyHealthInsurance) {
      supportDeductionDetails.longTermCare = truncateToTen(supportDeductionDetails.healthInsurance * insuranceRates.longTermCare);
      supportDeduction += supportDeductionDetails.longTermCare;
    }

    // 고용보험 (근로자 부담) - 체크박스 상태가 우선, 원단위 절삭 적용
    if (canApplyEmployment) {
      supportDeductionDetails.employmentInsurance = truncateToTen(supportDeductionBase * insuranceRates.employmentInsurance);
      supportDeduction += supportDeductionDetails.employmentInsurance;
    }

    // 실업급여 (사업주 부담) - 실업급여 체크박스 (65세 이상은 별도 선택 가능), 원단위 절삭 적용
    if (canApplyUnemployment) {
      supportDeductionDetails.unemployment = truncateToTen(supportDeductionBase * insuranceRates.unemployment);
      supportDeduction += supportDeductionDetails.unemployment;
    }

    // 산재보험 (사업주 부담) - 항상 적용, 원단위 절삭 적용
    supportDeductionDetails.industrialAccident = truncateToTen(supportDeductionBase * insuranceRates.industrialAccident);
    supportDeduction += supportDeductionDetails.industrialAccident;

    // 퇴직금 부분 ((장기근속수당 + 중증수급자가산) ÷ 12) - 체크박스 상태가 우선, 원단위 절삭 적용
    // 기관부담금의 퇴직금은 totalPay/12로 계산되므로, 지원공제에서도 동일하게 supportDeductionBase를 사용
    if (supportDeductionBase > 0 && settings.applyRetirement) {
      supportDeductionDetails.retirement = truncateToTen(supportDeductionBase / 12);
      supportDeduction += supportDeductionDetails.retirement;
    }
  }

  // 지원공제를 근로자 공제에 추가 (TabYoboWage.vue 로직과 동일)
  totalEmployeeDeduction += supportDeduction;

  const totalCenterDeduction =
    centerDeductions.nationalPension +
    centerDeductions.healthInsurance +
    centerDeductions.longTermCare +
    centerDeductions.employmentInsurance +
    centerDeductions.unemployment +
    centerDeductions.industrialAccident +
    centerDeductions.retirement;

  // 실수령액 (본인부담금공제, 기타공제 포함 - totalEmployeeDeduction에 이미 합산됨)
  const netPay = totalPay - totalEmployeeDeduction;

  // 비과세 효과 계산 (TabYoboWage.vue 로직과 동일하게 적용)
  let employeeTaxSavings = 0;
  let centerTaxSavings = 0;
  
  if (nonTaxableAmount > 0 && taxData.value) {
    // === 근로자 부담 절감액 (실수령액 증가액) ===
    // 1. 소득세, 지방세 절감 (비과세를 과세로 계산했을 경우와 비교)
    const taxableWithNonTax = taxableAmount + nonTaxableAmount;
    const incomeTaxWithNonTax = calculateIncomeTax(taxableWithNonTax);
    const localTaxWithNonTax = Math.round(incomeTaxWithNonTax * 0.1);
    let taxSavings = (incomeTaxWithNonTax + localTaxWithNonTax) - (employeeDeductions.incomeTax + employeeDeductions.localTax);
    
    // 세금 절약액이 0이거나 음수인 경우 최소 세율 적용
    if (taxSavings <= 0) {
      const estimatedTaxRate = 0.0726;
      taxSavings = Math.round(nonTaxableAmount * estimatedTaxRate);
    }
    
    // 2. 국민연금 절감 (근로자 부담분)
    const pensionSavings = canApplyPension ? truncateToTen(nonTaxableAmount * (taxData.value.tr_yeon || 0.045)) : 0;
    
    // 3. 건강보험 + 장기요양보험 절감 (근로자 부담분)
    const healthInsuranceSavings = settings.applyHealthInsurance ? truncateToTen(nonTaxableAmount * (taxData.value.tr_gun || 0.03545)) : 0;
    const longTermCareSavings = truncateToTen(healthInsuranceSavings * (taxData.value.tr_jang || 0.1295));
    
    // 4. 고용보험 절감 (근로자 부담분)
    const employmentInsuranceSavings = canApplyEmployment ? truncateToTen(nonTaxableAmount * (taxData.value.tr_go || 0.009)) : 0;
    
    // 근로자 부담 총 절감액 (실수령액 증가액)
    employeeTaxSavings = Math.max(0,
      taxSavings +
      pensionSavings +
      healthInsuranceSavings +
      longTermCareSavings +
      employmentInsuranceSavings
    );
    
    // === 기관 부담 절감액 (인건비 절감액) ===
    // 1. 국민연금 절감 (사업주 부담분) - 체크박스 활성화 시에만
    const centerPensionSavings = canApplyPension ? truncateToTen(nonTaxableAmount * (taxData.value.tr_yeon || 0.045)) : 0;
    
    // 2. 건강보험 + 장기요양보험 절감 (사업주 부담분) - 항상 적용
    const centerHealthSavings = truncateToTen(nonTaxableAmount * (taxData.value.tr_gun || 0.03545));
    const centerLongTermCareSavings = truncateToTen(centerHealthSavings * (taxData.value.tr_jang || 0.1295));
    
    // 3. 고용보험 절감 (사업주 부담분) - 65세 미만 & 적용 시에만
    const centerEmploymentSavings = canApplyEmployment ? truncateToTen(nonTaxableAmount * (taxData.value.tr_go || 0.009)) : 0;
    
    // 4. 실업급여 절감 (사업주 부담분) - 나이와 상관없이 항상 적용
    const unemploymentSavings = truncateToTen(nonTaxableAmount * (taxData.value.tr_sil || 0.0025));
    
    // 5. 산재보험 절감 (사업주 부담분) - 나이와 상관없이 항상 적용
    const industrialAccidentSavings = truncateToTen(nonTaxableAmount * (taxData.value.tr_san || 0.0178));
    
    centerTaxSavings = Math.max(0,
      centerPensionSavings +
      centerHealthSavings +
      centerLongTermCareSavings +
      centerEmploymentSavings +
      unemploymentSavings +
      industrialAccidentSavings
    );
  }

  // 툴팁에서 사용할 계산 정보
  const calcMinWage = taxData.value?.tr_time_pay || 0;
  const calcAnnualLeaveDays = getAnnualLeaveDays(group.workStartDate);
  const calcActualWorkDays = getActualWorkDays(groupIndex);
  const calcWorkDaysForCalc = calcActualWorkDays > 0 ? calcActualWorkDays : serviceDays;
  const calcBaseVacationPay = calcWorkDaysForCalc > 0 
    ? Math.round((calcMinWage * totalSugaHours / calcWorkDaysForCalc) * calcAnnualLeaveDays / 12) 
    : 0;
  
  // 60시간 미만 여부 및 연차수당 조정 정보
  const isUnder60Hours = totalSugaHours < 60 && !isFamilyOnlyCaregiver;
  const calcBasicPlusHoliday = basicPay + holidayPay;
  const maxAllowedForUnder60 = totalMonthlyPay;
  const wasVacationPayAdjusted = isUnder60Hours && (calcBasicPlusHoliday + calcBaseVacationPay > maxAllowedForUnder60);
  const adjustedVacationPayAmount = wasVacationPayAdjusted ? Math.max(0, maxAllowedForUnder60 - calcBasicPlusHoliday) : 0;
  
  // 60시간 미만 시급 역산 정보
  let adjustedHourlyWage = calcMinWage;
  let wasWageAdjusted = false;
  let adjustedBaseVacationPay = 0;  // 역산 시급으로 계산된 기본 연차수당
  let vacationPayDifference = 0;    // 추가된 차액
  
  if (isUnder60Hours && totalSugaHours > 0) {
    const minWageBasicPay = calcMinWage * totalSugaHours;
    const minWageHolidayPay = Math.round(minWageBasicPay * 0.2);
    const minWageVacationPay = calcWorkDaysForCalc > 0 
      ? Math.round((calcMinWage * totalSugaHours / calcWorkDaysForCalc) * calcAnnualLeaveDays / 12) 
      : 0;
    const minWageTotal = minWageBasicPay + minWageHolidayPay + minWageVacationPay;
    
    // 최저시급 기준 기본급+주휴수당+연차수당 < 합계금액인 경우 시급 역산 적용
    if (minWageTotal < totalMonthlyPay) {
      // 연차수당까지 포함한 역산 시급 계산
      const calcVacationRatio = calcWorkDaysForCalc > 0 ? (calcAnnualLeaveDays / 12) / calcWorkDaysForCalc : 0;
      const calcTotalRatio = 1 + 0.2 + calcVacationRatio;
      const theoreticalMaxWage = totalMonthlyPay / (totalSugaHours * calcTotalRatio);
      const calculatedAdjustedWage = Math.floor(theoreticalMaxWage / 10) * 10;
      
      if (calculatedAdjustedWage > calcMinWage) {
        adjustedHourlyWage = calculatedAdjustedWage;
        wasWageAdjusted = true;
        
        // 역산 시급으로 계산된 기본 연차수당
        adjustedBaseVacationPay = calcWorkDaysForCalc > 0 
          ? Math.round((calculatedAdjustedWage * totalSugaHours / calcWorkDaysForCalc) * calcAnnualLeaveDays / 12) 
          : 0;
        
        // 추가된 차액 = 최종 연차수당 - 역산 시급 기준 연차수당
        vacationPayDifference = vacationPay - adjustedBaseVacationPay;
      }
    }
  }
  
  const calcRemainingAmount = isUnder60Hours ? 0 : Math.max(0, totalMonthlyPay - (calcMinWage * totalSugaHours) - Math.round(calcMinWage * totalSugaHours * 0.2) - calcBaseVacationPay);
  const calcVacationPayAddition = isUnder60Hours ? 0 : (vacationPay - calcBaseVacationPay);
  
  // 근속 정보 계산
  const workPeriodForCalc = props.yearMonth ? calculateWorkPeriod(group.workStartDate, props.yearMonth) : { years: 0, months: 0 };
  const calcTotalMonthsWorked = workPeriodForCalc.years * 12 + workPeriodForCalc.months;
  const calcYearLevel = calcTotalMonthsWorked < 12 ? 1 : Math.floor(calcTotalMonthsWorked / 12) + 1;
  
  // 휴일/연장 근무 여부 및 시간
  const holidayMinutes = getHolidayTotalMinutes(groupIndex);
  const overtimeMinutes = getOvertimeTotalMinutes(groupIndex);
  const hasHolidayWork = calculateHolidayAllowanceAmount(groupIndex) > 0 || getHolidayCount(groupIndex) > 0;
  const hasOvertimeWork = calculateOvertimeAllowanceAmount(groupIndex) > 0;

  return {
    isFamilyOnlyCaregiver,
    avgTimePay,
    basicPay,
    holidayPay,
    vacationPay,
    mealAllowance: actualMealAllowance,
    basicTotal,
    longServicePay,
    etcAllowance,
    severeCareAddition,
    holidayAllowance,
    overtimeAllowance,
    totalPay,
    taxableAmount,
    nonTaxableAmount,
    employeeDeductions,
    centerDeductions,
    supportDeduction,
    boninPayAmount,
    empEtcAmount: empEtcAmountForCalc,
    totalEmployeeDeduction,
    totalCenterDeduction,
    netPay,
    employeeTaxSavings,
    centerTaxSavings,
    suggestedNationalPension,
    calcInfo: {
      minWage: calcMinWage,
      totalSugaHours,
      annualLeaveDays: calcAnnualLeaveDays,
      workDaysForCalc: calcWorkDaysForCalc,
      totalMonthlyPay,
      baseVacationPay: calcBaseVacationPay,
      remainingAmount: calcRemainingAmount,
      vacationPayAddition: calcVacationPayAddition,
      yearLevel: calcYearLevel,
      totalMonthsWorked: calcTotalMonthsWorked,
      mealAllowance: actualMealAllowance,
      hasHolidayWork,
      hasOvertimeWork,
      holidayMinutes,
      overtimeMinutes,
      supportDeductionDetails,
      isUnder60Hours,
      wasVacationPayAdjusted,
      adjustedVacationPayAmount,
      basicPlusHoliday: calcBasicPlusHoliday,
      adjustedHourlyWage,
      wasWageAdjusted,
      adjustedBaseVacationPay,
      vacationPayDifference
    }
  };
};

// 서비스 내역 조회
const fetchServiceData = async () => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code || !props.yearMonth) {
    return;
  }

  try {
    const [individualRes, dailyRes, weeklyRes, severeCareRes] = await Promise.all([
      axios.get('/api/calc/caregiver-individual-services', {
        params: {
          sisulCode: user.m_sisul_code,
          lebYm: props.yearMonth
        },
        withCredentials: true
      }),
      axios.get('/api/calc/caregiver-daily-services', {
        params: {
          sisulCode: user.m_sisul_code,
          lebYm: props.yearMonth
        },
        withCredentials: true
      }),
      axios.get('/api/calc/caregiver-weekly-services', {
        params: {
          sisulCode: user.m_sisul_code,
          lebYm: props.yearMonth
        },
        withCredentials: true
      }),
      axios.get('/api/calc/severe-care-addition-daily', {
        params: {
          sisulCode: user.m_sisul_code,
          lebYm: props.yearMonth
        },
        withCredentials: true
      })
    ]);

    // 개별 서비스 데이터 그룹별로 분류
    const individualData = individualRes.data.data || [];
    const dailyData = dailyRes.data.data || [];
    const weeklyData = weeklyRes.data.data || [];
    const severeCareData = severeCareRes.data.data || [];

    groupedMemberData.value.forEach((group, groupIndex) => {
      // 개별 서비스 내역
      individualServices.value[groupIndex] = individualData.filter((service: any) =>
        service.caregiver_name === group.name &&
        service.caregiver_birth === group.birth
      );

      // 중증수급자 가산 일자별 데이터
      severeCareAdditionDaily.value[groupIndex] = severeCareData.filter((service: any) =>
        service.caregiver_name === group.name &&
        service.caregiver_birth === group.birth
      );

      // 주차별 근무내역 (일자별 + 주차별 통합)
      const dailyForGroup = dailyData.filter((service: any) =>
        service.caregiver_name === group.name &&
        service.caregiver_birth === group.birth
      );

      const weeklyForGroup = weeklyData.filter((service: any) =>
        service.caregiver_name === group.name &&
        service.caregiver_birth === group.birth
      );

      // 주차별로 그룹화
      const weeklyMap = new Map();
      weeklyForGroup.forEach((week: any) => {
        weeklyMap.set(week.service_week, {
          service_week: week.service_week,
          weekly_total_hours: week.weekly_total_hours,
          daily_items: []
        });
      });

      dailyForGroup.forEach((daily: any) => {
        if (weeklyMap.has(daily.service_week)) {
          weeklyMap.get(daily.service_week).daily_items.push(daily);
        }
      });

      combinedWeeklyServices.value[groupIndex] = Array.from(weeklyMap.values());

      // 급여 상세 계산
      payrollDetails.value[groupIndex] = calculatePayrollDetails(group, groupIndex);

      // 국민연금 자동 설정 (DB에 설정된 적이 없는 경우만)
      // _hasYeonPaySetting 플래그가 true이면 이미 DB에서 불러온 설정이 있으므로 자동 설정 안 함
      const settings = settingsData.value[groupIndex];
      const payroll = payrollDetails.value[groupIndex];
      if (settings && payroll && !settings.applyNationalPension && !settings._hasYeonPaySetting) {
        // 가족요양만 진행하는 요양보호사이면서 60시간 미만이면 자동 설정 안 함
        const isFamilyOnly = isAllFamilyCaregiver(group);
        const totalSugaHours = getTotalSugaHours(group, groupIndex);
        
        if (isFamilyOnly && totalSugaHours < 60) {
          // 가족요양 60시간 미만 - 국민연금 자동 설정 하지 않음
        } else {
          // 만 60세 미만일 때만 체크박스 자동 활성화 + 값 자동 설정
          const caregiverAge = calculateAge(group.birth, props.yearMonth || '');
          if (caregiverAge < 60 && payroll.suggestedNationalPension > 0) {
            settings.applyNationalPension = true;
            settings.yeonPay = new Intl.NumberFormat('ko-KR').format(payroll.suggestedNationalPension);
          }
        }
      }
    });
    
    // 원래 설정 저장 (변경사항 비교용)
    saveOriginalSettings();
  } catch (error) {
    console.error('서비스 데이터 조회 오류:', error);
  }
};

// Member Info 데이터 조회 (caregiver-member-join API 사용)
const fetchMemberInfo = async () => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code || !props.yearMonth) {
    return;
  }

  try {
    isLoadingMemberInfo.value = true;
    
    // 최저시급 데이터 먼저 가져오기
    const year = props.yearMonth.slice(0, 4);
    const taxResponse = await axios.get(`/api/calc/tax/${year}`);
    if (taxResponse.data.success) {
      taxData.value = taxResponse.data.data.taxRate;
      sojiData.value = taxResponse.data.data.soji || [];
    }
    
    // 공휴일 데이터 로드
    await loadHolidays(year);
    
    // TabYoboWage와 동일한 API 사용 (중증수급자 가산 데이터 포함)
    const [response, detailResponse] = await Promise.all([
      axios.get('/api/calc/caregiver-member-join', {
        params: {
          sisulCode: user.m_sisul_code,
          lebYm: props.yearMonth
        },
        withCredentials: true
      }),
      axios.get('/api/calc/caregiver-member-join-detail', {
        params: {
          sisulCode: user.m_sisul_code,
          lebYm: props.yearMonth
        },
        withCredentials: true
      })
    ]);

    // 중증수급자 가산 데이터 저장
    if (detailResponse.data.success || detailResponse.data.data) {
      memberJoinDetailData.value = detailResponse.data.data || [];
    } else {
      memberJoinDetailData.value = [];
    }

    if (response.data.success || response.data.data) {
      const rawData = response.data.data || [];
      
      
      // caregiver-member-join API 데이터 구조에 맞게 매핑
      memberInfoData.value = rawData.map((item: any) => ({
        lemi_idx: item.lemi_idx,
        emp_code: item.emp_code || '',  // hh_emp 테이블의 emp_code 사용
        lemi_name: item.caregiver_name,
        lemi_birth: item.caregiver_birth,
        lemi_gender: item.lem_gender,
        lemi_license: item.lem_license,
        lemi_work_start_date: item.lem_work_start_date,
        lemi_work_end_date: item.lem_work_end_date,
        lemi_patient_code: item.leb_patient_code,
        lemi_suga_code: item.suga_code,
        lemi_time_pay: item.time_pay,
        lemi_yeon_pay: item.lemi_yeon_pay,
        lemi_bonin_pay: item.lemi_bonin_pay,
        lemi_emp_etc: item.lemi_emp_etc,
        lemi_apply_go: item.lemi_apply_go,
        lemi_apply_holiday_pay: item.lemi_apply_holiday_pay,
        lemi_apply_lunch_pay: item.lemi_apply_lunch_pay,
        lemi_apply_gun: item.lemi_apply_gun,
        lemi_apply_overtime_pay: item.lemi_apply_overtime_pay,
        lemi_apply_out_pay: item.lemi_apply_out_pay,
        lemi_apply_labor_day_pay: item.lemi_apply_labor_day_pay,
        lemi_long_service_pay: item.lemi_long_service_pay,
        lemi_etc_allowance: item.lemi_etc_allowance,  // 기타수당
        lemi_sil_pay: item.lemi_sil_pay,  // 실업급여 금액
        lemi_custom_suga_hours: item.lemi_custom_suga_hours,  // 수동 조정된 수가시간 (JSON)
        lem_isboss: item.lem_isboss,
        patient_name: item.leb_patient_name,
        suga_name: item.suga_name,
        service_days: item.service_days,
        suga_hours: item.suga_hours,
        total_suga_money: item.total_suga_money,
        lunch_time_count: item.lunch_time_count || 0
      }));
      
      initializeSettingsData();
      
      // 서비스 데이터 조회
      await fetchServiceData();
      
      // 누적 퇴직금 조회
      await fetchCumulativeRetirement();
    } else {
      memberInfoData.value = [];
    }
  } catch (error) {
    console.error('사원 급여 정보 조회 오류:', error);
    memberInfoData.value = [];
  } finally {
    isLoadingMemberInfo.value = false;
  }
};

// 날짜가 일요일인지 확인
const isSunday = (dateStr: string) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  return date.getDay() === 0;
};

// 공휴일 이름 가져오기
const getHolidayName = (dateStr: string) => {
  if (!dateStr) return null;
  return holidaysData.value[dateStr] || null;
};

// 공휴일 데이터 불러오기
const loadHolidays = async (year: string) => {
  try {
    const response = await axios.get(`/api/calc/holidays/${year}`);
    if (response.data.success) {
      const holidayMap: Record<string, string> = {};
      response.data.data.forEach((holiday: any) => {
        holidayMap[holiday.hh_date.split('T')[0]] = holiday.hh_holiday_name;
      });
      holidaysData.value = holidayMap;
    }
  } catch (error) {
    console.error('공휴일 조회 오류:', error);
  }
};

// 누적 퇴직금 조회 (해당 년도 1월부터 현재 월까지의 퇴직금 합계)
const fetchCumulativeRetirement = async () => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code || !props.yearMonth) {
    return;
  }

  // 그룹화된 멤버 데이터에서 고유한 직원명 추출
  const uniqueNames = [...new Set(groupedMemberData.value.map((group: any) => group.name))];

  if (uniqueNames.length === 0) {
    return;
  }

  try {
    const promises = uniqueNames.map((empName: string) =>
      axios.get('/api/calc/cumulative-retirement', {
        params: {
          sisulCode: user.m_sisul_code,
          yearMonth: props.yearMonth,
          empName: empName
        },
        withCredentials: true
      })
    );

    const results = await Promise.allSettled(promises);

    const retirementMap: Record<string, number> = {};
    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value.data.success) {
        retirementMap[uniqueNames[index]] = Number(result.value.data.data) || 0;
      }
    });

    // 반응성을 위해 개별적으로 할당
    Object.keys(retirementMap).forEach(key => {
      cumulativeRetirement.value[key] = retirementMap[key];
    });
  } catch (error) {
    console.error('누적 퇴직금 조회 오류:', error);
  }
};

// 중증수급자 가산 데이터 (그룹별로 매칭)
const getSevereCareAdditionForGroup = (groupIndex: number) => {
  if (!memberJoinDetailData.value || memberJoinDetailData.value.length === 0) return [];
  if (!groupedMemberData.value || !groupedMemberData.value[groupIndex]) return [];

  const group = groupedMemberData.value[groupIndex];
  const caregiverName = group.name;
  const caregiverBirth = group.birth;

  // 해당 요양보호사의 중증수급자 가산 데이터 필터링
  // suga_name이 '중증수급자 가산' 또는 '중증수급자가산'인 경우 모두 포함
  return memberJoinDetailData.value.filter((item: any) =>
    item.caregiver_name === caregiverName &&
    item.caregiver_birth === caregiverBirth &&
    (item.suga_name === '중증수급자 가산' || item.suga_name === '중증수급자가산' || 
     (item.suga_name && item.suga_name.includes('중증수급자')))
  );
};

// 그룹별 중증수급자 가산 총액 계산
const getSevereCareAdditionTotal = (groupIndex: number) => {
  const severeAdditions = getSevereCareAdditionForGroup(groupIndex);
  if (!severeAdditions || severeAdditions.length === 0) return 0;

  let total = 0;
  severeAdditions.forEach((item: any) => {
    total += parseFloat(item.total_suga_money || 0);
  });

  return total;
};

// 그룹별 휴일 근무 일수 계산
const getHolidayCount = (groupIndex: number) => {
  const services = individualServices.value[groupIndex];
  if (!services || services.length === 0) return 0;

  const settings = settingsData.value[groupIndex];
  if (!settings?.applyHoliday) return 0;

  const holidayDates = new Set<string>();
  const year = props.yearMonth?.substring(0, 4) || '';
  const yearNum = parseInt(year);
  
  // 2025년 이하: 근로자의 날(5/1) 제외, 2026년 이상: 일반 휴일로 포함
  const laborDay = `${year}-05-01`;
  const excludeLaborDay = yearNum <= 2025;

  services.forEach((service: any) => {
    const dateStr = service.service_date;
    // 2025년 이하에서는 근로자의날 제외
    if (excludeLaborDay && dateStr === laborDay) return;
    // 일요일이거나 공휴일인 경우
    if (isSunday(dateStr) || getHolidayName(dateStr)) {
      holidayDates.add(dateStr);
    }
  });

  return holidayDates.size;
};

// 수가명이 휴일가산 수가인지 확인 (공휴일가산, 일요일가산)
const isHolidaySugaName = (sugaName: string | null | undefined): boolean => {
  if (!sugaName) return false;
  const name = sugaName.replace(/\s/g, ''); // 공백 제거
  if (name.includes('공휴일가산')) return true;
  if (name.includes('공휴일') && name.includes('가산')) return true;
  if (name.includes('일요일가산')) return true;
  if (name.includes('일요일') && name.includes('가산')) return true;
  return false;
};

// 휴일수당 금액 계산 (휴일 근무 시간 × 최저시급 × 0.5)
const calculateHolidayAllowanceAmount = (groupIndex: number) => {
  const settings = settingsData.value[groupIndex];
  if (!settings?.applyHoliday) return 0;

  const totalMinutes = getHolidayTotalMinutes(groupIndex);
  if (totalMinutes === 0) return 0;

  const timePay = taxData.value?.tr_time_pay || 0;
  if (timePay === 0) return 0;

  const totalHours = totalMinutes / 60;
  return Math.round(totalHours * timePay * 0.5);
};

// 그룹별 휴일 근무 총 시간(분) 계산
const getHolidayTotalMinutes = (groupIndex: number) => {
  const settings = settingsData.value[groupIndex];
  if (!settings?.applyHoliday) return 0;

  const group = groupedMemberData.value[groupIndex];
  if (!group?.patients) return 0;

  let totalMinutes = 0;

  group.patients.forEach((patient: any) => {
    if (!isHolidaySugaName(patient.sugaName)) return;

    const sugaKey = getSugaKey(patient);
    const sugaHours = settings.customSugaHours?.[patient.patientCode]?.[sugaKey] ?? patient.sugaHours ?? 0;
    const serviceDays = patient.serviceDays || 0;
    totalMinutes += serviceDays * sugaHours * 60;
  });

  return totalMinutes;
};

// 그룹별 연장 근무 총 시간(분) 계산 (일일 8시간 초과분)
const getOvertimeTotalMinutes = (groupIndex: number) => {
  const dailyData = combinedWeeklyServices.value[groupIndex];
  if (!dailyData || dailyData.length === 0) return 0;

  const settings = settingsData.value[groupIndex];
  if (!settings?.applyOvertime) return 0;

  let totalOvertimeMinutes = 0;

  // 주차별 데이터에서 일자별 데이터 추출
  dailyData.forEach((weekGroup: any) => {
    weekGroup.daily_items?.forEach((daily: any) => {
      const dailyMinutes = (daily.daily_total_hours || 0) * 60;
      // 일일 8시간(480분) 초과분만 연장근무
      if (dailyMinutes > 480) {
        totalOvertimeMinutes += (dailyMinutes - 480);
      }
    });
  });

  return Math.round(totalOvertimeMinutes);
};

// 연장수당 금액 계산 (연장 근무 시간 × 최저시급 × 0.5)
const calculateOvertimeAllowanceAmount = (groupIndex: number) => {
  const settings = settingsData.value[groupIndex];
  if (!settings?.applyOvertime) return 0;

  const totalMinutes = getOvertimeTotalMinutes(groupIndex);
  if (totalMinutes === 0) return 0;

  const timePay = taxData.value?.tr_time_pay || 0;
  if (timePay === 0) return 0;

  const totalHours = totalMinutes / 60;
  return Math.round(totalHours * timePay * 0.5);
};

// 분을 시간으로 포맷팅
const formatTotalMinutes = (minutes: number) => {
  if (!minutes) return '0분';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) {
    return `${hours}시간 ${mins}분`;
  } else if (hours > 0) {
    return `${hours}시간`;
  } else {
    return `${mins}분`;
  }
};

// 설정 저장
const saveSettings = async () => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code || !props.yearMonth) {
    store.commit('showSnackbar', { text: '사용자 정보를 확인할 수 없습니다.', color: 'error' });
    return;
  }

  try {
    isSaving.value = true;

    const saveData: any[] = [];
    
    groupedMemberData.value.forEach((group, groupIndex) => {
      const settings = settingsData.value[groupIndex];
      if (!settings) return;
      
      group.originalItems.forEach((item, itemIndex) => {
        const patientCode = item.lemi_patient_code;
        const sugaCode = item.lemi_suga_code;
        
        // patients 배열에서 같은 인덱스의 항목을 가져와서 getSugaKey 사용 (키 일관성 보장)
        const patient = group.patients[itemIndex];
        const sugaKey = patient ? getSugaKey(patient) : sugaCode;
        
        const timePay = settings.timePays[patientCode]?.[sugaKey] || '';
        const numericTimePay = timePay ? String(timePay).replace(/[^\d]/g, '') : '';
        
        // 퇴직금 금액 계산 (payrollDetails에서 가져옴)
        const payroll = payrollDetails.value[groupIndex];
        const retirementAmount = payroll?.centerDeductions?.retirement || 0;
        
        // 실업급여 금액 직접 계산 (settings.applyUnemployment 기준으로)
        let unemploymentAmount = 0;
        if (settings.applyUnemployment && payroll) {
          const taxableAmount = payroll.taxableAmount || 0;
          const unemploymentRate = taxData.value?.tr_sil || 0.0025;
          unemploymentAmount = Math.round(taxableAmount * unemploymentRate);
        }
        
        // 수동 조정된 수가시간을 JSON으로 변환 (해당 요양보호사의 전체 customSugaHours)
        const customSugaHoursJson = settings.customSugaHours && Object.keys(settings.customSugaHours).length > 0
          ? JSON.stringify(settings.customSugaHours)
          : null;
        
        saveData.push({
          m_sisul_code: user.m_sisul_code,
          lemi_ym: props.yearMonth,
          lemi_name: item.lemi_name,
          lemi_time_pay: numericTimePay,
          lemi_birth: item.lemi_birth,
          lemi_gender: item.lemi_gender,
          lemi_license: item.lemi_license,
          lemi_work_start_date: item.lemi_work_start_date,
          lemi_work_end_date: item.lemi_work_end_date,
          lemi_patient_code: patientCode,
          lemi_suga_code: sugaCode,
          lemi_yeon_pay: settings.applyNationalPension && settings.yeonPay ? String(settings.yeonPay).replace(/[^\d]/g, '') : '',
          lemi_bonin_pay: settings.applyBoninPay && settings.boninPay ? String(settings.boninPay).replace(/[^\d]/g, '') : '',
          lemi_emp_etc: settings.applyEmpEtc && settings.empEtc ? String(settings.empEtc).replace(/[^\d]/g, '') : '',
          lemi_apply_gun: settings.applyHealthInsurance ? 'Y' : 'N',
          lemi_apply_go: settings.applyEmploymentInsurance ? 'Y' : 'N',
          lemi_apply_holiday_pay: settings.applyHoliday ? 'Y' : 'N',
          lemi_apply_overtime_pay: settings.applyOvertime ? 'Y' : 'N',
          lemi_apply_lunch_pay: settings.applyMeal ? 'Y' : 'N',
          lemi_apply_out_pay: settings.applyRetirement ? 'Y' : 'N',
          lemi_out_pay: retirementAmount > 0 ? String(retirementAmount) : '',
          lemi_sil_pay: unemploymentAmount > 0 ? String(unemploymentAmount) : '', // 실업급여 금액
          lemi_apply_labor_day_pay: 'Y', // 근로자의날 수당은 기본 적용
          lemi_long_service_pay: settings.applyLongServicePay && settings.longServicePay ? String(settings.longServicePay).replace(/[^\d]/g, '') : '',
          lemi_etc_allowance: settings.applyEtcAllowance && settings.etcAllowance ? String(settings.etcAllowance).replace(/[^\d]/g, '') : '',
          lemi_custom_suga_hours: customSugaHoursJson  // 수동 조정된 수가시간 (JSON)
        });
      });
    });

    // 1. 기존 설정 데이터 저장 (hh_longterm_excel_member_info)
    const response = await axios.post('/api/calc/save-member-info', {
      sisulCode: user.m_sisul_code,
      lebYm: props.yearMonth,
      memberData: saveData
    }, { withCredentials: true });

    if (response.data.success) {
      // 2. 계산된 급여 데이터 저장 (hh_emp_sabok_work_info)
      const payrollDataList = groupedMemberData.value.map((group, groupIndex) => {
        const payroll = payrollDetails.value[groupIndex];
        const settings = settingsData.value[groupIndex];
        if (!payroll) return null;

        const totalSugaHours = getTotalSugaHours(group, groupIndex);  // 수동 조정된 시간 반영
        const serviceDays = getTotalServiceDays(group);
        const age = calculateAge(group.birth, props.yearMonth || '');
        
        // 기관수익 계산: 수가금액 - 총지급액 - 기관공제
        const centerProfit = (group.totalSugaMoney || 0) - (payroll.totalPay || 0) - (payroll.totalCenterDeduction || 0);
        
        // 수가시간 수동 변경 여부 확인
        const sugaHoursChanged = settings?.sugaHoursChanged || false;
        
        return {
          empCode: group.empCode || '',  // emp_code로 직원 식별
          sugaHours: totalSugaHours,
          sugaHoursChanged: sugaHoursChanged ? 'Y' : 'N',  // 수가시간 수동 변경 여부
          serviceDays: serviceDays,
          totalMinutes: Math.round(totalSugaHours * 60),
          lunchTimeCount: group.totalLunchTimeCount || 0,
          avgTimePay: payroll.avgTimePay || 0,
          isFamilyOnly: payroll.isFamilyOnlyCaregiver || false,
          isBoss: group.isBoss || false,
          monthlyPay: group.totalSugaMoney || 0,  // 월급여 (수가금액 합계)
          basicPay: payroll.basicPay || 0,
          holidayPay: payroll.holidayPay || 0,         // 주휴수당
          vacationPay: payroll.vacationPay || 0,       // 연차수당
          mealAllowance: payroll.mealAllowance || 0,
          holidayAllowance: payroll.holidayAllowance || 0, // 휴일수당
          overtimeAllowance: payroll.overtimeAllowance || 0,
          nightAllowance: 0,
          laborDayAllowance: 0,
          longServicePay: payroll.longServicePay || 0,
          etcAllowance: payroll.etcAllowance || 0,
          severeCareAddition: payroll.severeCareAddition || 0,
          otherPay: 0,
          totalPay: payroll.totalPay || 0,
          holidayMinutes: getHolidayTotalMinutes(groupIndex),
          overtimeMinutes: getOvertimeTotalMinutes(groupIndex),
          nightMinutes: 0,
          employeeDeductions: payroll.employeeDeductions || {},
          totalEmployeeDeduction: payroll.totalEmployeeDeduction || 0,
          centerDeductions: payroll.centerDeductions || {},
          totalCenterDeduction: payroll.totalCenterDeduction || 0,
          supportDeduction: payroll.supportDeduction || 0,
          boninPay: payroll.boninPayAmount || 0,  // 본인부담금공제
          empEtc: payroll.empEtcAmount || 0,      // 기타공제
          netPay: payroll.netPay || 0,
          taxableAmount: payroll.taxableAmount || 0,
          nonTaxableAmount: payroll.nonTaxableAmount || 0,
          employeeTaxSavings: payroll.employeeTaxSavings || 0,
          centerTaxSavings: payroll.centerTaxSavings || 0,
          centerProfit: centerProfit  // 기관수익
        };
      }).filter(item => item !== null);

      try {
        await axios.post('/api/calc/yobo-payroll', {
          sisulCode: user.m_sisul_code,
          yearMonth: props.yearMonth,
          payrollDataList
        }, { withCredentials: true });
      } catch (payrollError) {
        console.warn('급여 계산 결과 저장 실패 (무시됨):', payrollError);
      }

      store.commit('showSnackbar', { text: `✅ 설정이 저장되었습니다. (${response.data.savedCount}건)`, color: 'success' });
      hasUnsavedChanges.value = false;
      
      // customSugaHours 백업 (DB에 저장되지 않으므로 보존 필요)
      const customSugaHoursBackup: Record<number, { customSugaHours: Record<string, Record<string, number>>; sugaHoursChanged: boolean }> = {};
      Object.keys(settingsData.value).forEach((key) => {
        const idx = Number(key);
        if (settingsData.value[idx]?.customSugaHours && Object.keys(settingsData.value[idx].customSugaHours).length > 0) {
          customSugaHoursBackup[idx] = {
            customSugaHours: JSON.parse(JSON.stringify(settingsData.value[idx].customSugaHours)),
            sugaHoursChanged: settingsData.value[idx].sugaHoursChanged
          };
        }
      });
      
      // 설정 데이터 초기화 후 다시 로드 (새로운 DB 값 반영을 위해)
      settingsData.value = {};
      await fetchMemberInfo();
      
      // customSugaHours 복원
      Object.keys(customSugaHoursBackup).forEach((key) => {
        const idx = Number(key);
        if (settingsData.value[idx]) {
          settingsData.value[idx].customSugaHours = customSugaHoursBackup[idx].customSugaHours;
          settingsData.value[idx].sugaHoursChanged = customSugaHoursBackup[idx].sugaHoursChanged;
        }
      });
    } else {
      store.commit('showSnackbar', { text: '설정 저장에 실패했습니다.', color: 'error' });
    }
  } catch (error) {
    console.error('설정 저장 오류:', error);
    store.commit('showSnackbar', { text: '설정 저장 중 오류가 발생했습니다.', color: 'error' });
  } finally {
    isSaving.value = false;
  }
};

// 개별 요양보호사 저장
const saveSingleCaregiver = async (groupIndex: number) => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code || !props.yearMonth) {
    store.commit('showSnackbar', { text: '사용자 정보를 확인할 수 없습니다.', color: 'error' });
    return;
  }

  const group = groupedMemberData.value[groupIndex];
  if (!group) {
    store.commit('showSnackbar', { text: '요양보호사 정보를 찾을 수 없습니다.', color: 'error' });
    return;
  }

  const settings = settingsData.value[groupIndex];
  if (!settings) {
    store.commit('showSnackbar', { text: '설정 정보를 찾을 수 없습니다.', color: 'error' });
    return;
  }

  try {
    isSaving.value = true;

    const saveData: any[] = [];
    
    // 퇴직금 금액 계산 (payrollDetails에서 가져옴)
    const payroll = payrollDetails.value[groupIndex];
    const retirementAmount = payroll?.centerDeductions?.retirement || 0;
    
    // 실업급여 금액 직접 계산 (settings.applyUnemployment 기준으로)
    let unemploymentAmount = 0;
    if (settings.applyUnemployment && payroll) {
      const taxableAmount = payroll.taxableAmount || 0;
      const unemploymentRate = taxData.value?.tr_sil || 0.0025;
      unemploymentAmount = Math.round(taxableAmount * unemploymentRate);
    }
    
    // 수동 조정된 수가시간을 JSON으로 변환 (해당 요양보호사의 전체 customSugaHours)
    const customSugaHoursJson = settings.customSugaHours && Object.keys(settings.customSugaHours).length > 0
      ? JSON.stringify(settings.customSugaHours)
      : null;
    
    group.originalItems.forEach((item, itemIndex) => {
      const patientCode = item.lemi_patient_code;
      const sugaCode = item.lemi_suga_code;
      
      // patients 배열에서 같은 인덱스의 항목을 가져와서 getSugaKey 사용 (키 일관성 보장)
      const patient = group.patients[itemIndex];
      const sugaKey = patient ? getSugaKey(patient) : sugaCode;
      
      const timePay = settings.timePays[patientCode]?.[sugaKey] || '';
      const numericTimePay = timePay ? String(timePay).replace(/[^\d]/g, '') : '';
      
      saveData.push({
        m_sisul_code: user.m_sisul_code,
        lemi_ym: props.yearMonth,
        lemi_name: item.lemi_name,
        lemi_time_pay: numericTimePay,
        lemi_birth: item.lemi_birth,
        lemi_gender: item.lemi_gender,
        lemi_license: item.lemi_license,
        lemi_work_start_date: item.lemi_work_start_date,
        lemi_work_end_date: item.lemi_work_end_date,
        lemi_patient_code: patientCode,
        lemi_suga_code: sugaCode,
        lemi_yeon_pay: settings.applyNationalPension && settings.yeonPay ? String(settings.yeonPay).replace(/[^\d]/g, '') : '',
        lemi_bonin_pay: settings.applyBoninPay && settings.boninPay ? String(settings.boninPay).replace(/[^\d]/g, '') : '',
        lemi_emp_etc: settings.applyEmpEtc && settings.empEtc ? String(settings.empEtc).replace(/[^\d]/g, '') : '',
        lemi_apply_gun: settings.applyHealthInsurance ? 'Y' : 'N',
        lemi_apply_go: settings.applyEmploymentInsurance ? 'Y' : 'N',
        lemi_apply_holiday_pay: settings.applyHoliday ? 'Y' : 'N',
        lemi_apply_overtime_pay: settings.applyOvertime ? 'Y' : 'N',
        lemi_apply_lunch_pay: settings.applyMeal ? 'Y' : 'N',
        lemi_apply_out_pay: settings.applyRetirement ? 'Y' : 'N',
        lemi_out_pay: retirementAmount > 0 ? String(retirementAmount) : '',
        lemi_sil_pay: unemploymentAmount > 0 ? String(unemploymentAmount) : '', // 실업급여 금액
        lemi_apply_labor_day_pay: 'Y',
        lemi_long_service_pay: settings.applyLongServicePay && settings.longServicePay ? String(settings.longServicePay).replace(/[^\d]/g, '') : '',
        lemi_etc_allowance: settings.applyEtcAllowance && settings.etcAllowance ? String(settings.etcAllowance).replace(/[^\d]/g, '') : '',
        lemi_custom_suga_hours: customSugaHoursJson  // 수동 조정된 수가시간 (JSON)
      });
    });

    const response = await axios.post('/api/calc/save-member-info', {
      sisulCode: user.m_sisul_code,
      lebYm: props.yearMonth,
      memberData: saveData
    }, { withCredentials: true });

    if (response.data.success) {
      store.commit('showSnackbar', { text: `✅ ${group.name} 요양보호사 설정이 저장되었습니다. (${response.data.savedCount}건)`, color: 'success' });
      saveOriginalSettings();
      hasUnsavedChanges.value = false;
    } else {
      store.commit('showSnackbar', { text: '설정 저장에 실패했습니다.', color: 'error' });
    }
  } catch (error) {
    console.error('개별 설정 저장 오류:', error);
    store.commit('showSnackbar', { text: '설정 저장 중 오류가 발생했습니다.', color: 'error' });
  } finally {
    isSaving.value = false;
  }
};

// 확정 버튼 클릭
const handleConfirm = () => {
  showConfirmDialog.value = true;
};

const proceedConfirm = async () => {
  showConfirmDialog.value = false;
  
  // StepThree 확정 시에는 마감 데이터를 생성하지 않고 emit만 수행
  store.commit('showSnackbar', { 
    text: '✅ 급여 설정이 확정되었습니다.', 
    color: 'success' 
  });
  emit('confirm');
};

// 해제 버튼 클릭
const handleRelease = () => {
  showReleaseDialog.value = true;
};

const proceedRelease = async () => {
  showReleaseDialog.value = false;
  
  const user = store.state.user;
  if (!user || !user.m_sisul_code || !props.yearMonth) {
    store.commit('showSnackbar', { text: '사용자 정보를 확인할 수 없습니다.', color: 'error' });
    return;
  }

  try {
    // 마감 해제 API 호출
    const response = await axios.put('/api/calc/wage-magam/release', {
      sisulCode: user.m_sisul_code,
      lebYm: props.yearMonth
    }, { withCredentials: true });

    if (response.data.success) {
      store.commit('showSnackbar', { 
        text: '✅ 급여 설정 확정이 해제되었습니다.', 
        color: 'success' 
      });
      emit('release');
    } else {
      store.commit('showSnackbar', { 
        text: '확정 해제에 실패했습니다.', 
        color: 'error' 
      });
    }
  } catch (error: any) {
    console.error('확정 해제 오류:', error);
    store.commit('showSnackbar', { 
      text: '확정 해제 중 오류가 발생했습니다: ' + (error.response?.data?.message || error.message), 
      color: 'error' 
    });
  }
};

// SetModal 이벤트 핸들러들
// 일괄 시급 적용 (일반)
const handleBulkTimePay = (value: string) => {
  const numericValue = value.replace(/[^\d]/g, '');
  
  groupedMemberData.value.forEach((group, groupIndex) => {
    group.patients.forEach((patient) => {
      // 가족 케어가 아닌 경우만 적용
      if (!patient.sugaName.includes('가족')) {
        if (!settingsData.value[groupIndex].timePays[patient.patientCode]) {
          settingsData.value[groupIndex].timePays[patient.patientCode] = {};
        }
        const sugaKey = getSugaKey(patient);
        settingsData.value[groupIndex].timePays[patient.patientCode][sugaKey] = 
          new Intl.NumberFormat('ko-KR').format(parseInt(numericValue));
      }
    });
  });
  
  checkUnsavedChanges();
  store.commit('showSnackbar', { text: '일반 시간당 임금이 일괄 적용되었습니다.', color: 'success' });
};

// 일괄 시급 적용 (가족)
const handleBulkTimePayFamily = (value: string) => {
  const numericValue = value.replace(/[^\d]/g, '');
  
  groupedMemberData.value.forEach((group, groupIndex) => {
    group.patients.forEach((patient) => {
      // 가족 케어인 경우만 적용
      if (patient.sugaName.includes('가족')) {
        if (!settingsData.value[groupIndex].timePays[patient.patientCode]) {
          settingsData.value[groupIndex].timePays[patient.patientCode] = {};
        }
        const sugaKey = getSugaKey(patient);
        settingsData.value[groupIndex].timePays[patient.patientCode][sugaKey] = 
          new Intl.NumberFormat('ko-KR').format(parseInt(numericValue));
      }
    });
  });
  
  checkUnsavedChanges();
  store.commit('showSnackbar', { text: '가족 케어 시간당 임금이 일괄 적용되었습니다.', color: 'success' });
};

// 일괄 시급 적용 (가족 60분)
const handleBulkTimePayFamily60 = (value: string) => {
  const numericValue = value.replace(/[^\d]/g, '');
  
  groupedMemberData.value.forEach((group, groupIndex) => {
    group.patients.forEach((patient) => {
      // 60분 + 가족 케어인 경우만 적용
      if (patient.sugaName.includes('60분') && patient.sugaName.includes('가족')) {
        if (!settingsData.value[groupIndex].timePays[patient.patientCode]) {
          settingsData.value[groupIndex].timePays[patient.patientCode] = {};
        }
        const sugaKey = getSugaKey(patient);
        settingsData.value[groupIndex].timePays[patient.patientCode][sugaKey] = 
          new Intl.NumberFormat('ko-KR').format(parseInt(numericValue));
      }
    });
  });
  
  checkUnsavedChanges();
  store.commit('showSnackbar', { text: '가족 60분 케어 시간당 임금이 일괄 적용되었습니다.', color: 'success' });
};

// 일괄 시급 적용 (가족 90분)
const handleBulkTimePayFamily90 = (value: string) => {
  const numericValue = value.replace(/[^\d]/g, '');
  
  groupedMemberData.value.forEach((group, groupIndex) => {
    group.patients.forEach((patient) => {
      // 90분 + 가족 케어인 경우만 적용
      if (patient.sugaName.includes('90분') && patient.sugaName.includes('가족')) {
        if (!settingsData.value[groupIndex].timePays[patient.patientCode]) {
          settingsData.value[groupIndex].timePays[patient.patientCode] = {};
        }
        const sugaKey = getSugaKey(patient);
        settingsData.value[groupIndex].timePays[patient.patientCode][sugaKey] = 
          new Intl.NumberFormat('ko-KR').format(parseInt(numericValue));
      }
    });
  });
  
  checkUnsavedChanges();
  store.commit('showSnackbar', { text: '가족 90분 케어 시간당 임금이 일괄 적용되었습니다.', color: 'success' });
};

// 일괄 휴일수당 적용
const handleBulkHolidayAllowance = (value: boolean) => {
  groupedMemberData.value.forEach((group, groupIndex) => {
    settingsData.value[groupIndex].applyHoliday = value;
  });
  
  const message = value ? '휴일수당이 일괄 지급으로 설정되었습니다.' : '휴일수당이 일괄 미지급으로 설정되었습니다.';
  store.commit('showSnackbar', { text: message, color: 'success' });
};

// 일괄 연장수당 적용
const handleBulkOvertimeAllowance = (value: boolean) => {
  groupedMemberData.value.forEach((group, groupIndex) => {
    settingsData.value[groupIndex].applyOvertime = value;
  });
  
  const message = value ? '연장수당이 일괄 지급으로 설정되었습니다.' : '연장수당이 일괄 미지급으로 설정되었습니다.';
  store.commit('showSnackbar', { text: message, color: 'success' });
};

// 일괄 퇴직금 적용
const handleBulkRetirement = (value: boolean) => {
  groupedMemberData.value.forEach((group, groupIndex) => {
    settingsData.value[groupIndex].applyRetirement = value;
  });
  
  const message = value ? '퇴직금이 일괄 적립으로 설정되었습니다.' : '퇴직금이 일괄 미적립으로 설정되었습니다.';
  store.commit('showSnackbar', { text: message, color: 'success' });
};

// 일괄 식대 적용
const handleBulkMealAllowance = (value: boolean) => {
  groupedMemberData.value.forEach((group, groupIndex) => {
    settingsData.value[groupIndex].applyMeal = value;
  });
  
  const message = value ? '식대가 일괄 지급으로 설정되었습니다.' : '식대가 일괄 미지급으로 설정되었습니다.';
  store.commit('showSnackbar', { text: message, color: 'success' });
};

// 장기근속수당 자동 계산
const handleBulkLongServiceAutoCalculate = () => {
  if (!props.yearMonth) return;
  
  groupedMemberData.value.forEach((group, groupIndex) => {
    const workPeriod = calculateWorkPeriod(group.workStartDate, props.yearMonth!);
    const totalMonths = workPeriod.years * 12 + workPeriod.months;
    
    let amount = 0;
    if (totalMonths >= 36) { // 3년 이상
      amount = 100000;
    } else if (totalMonths >= 24) { // 2년 이상
      amount = 50000;
    } else if (totalMonths >= 12) { // 1년 이상
      amount = 30000;
    }
    
    settingsData.value[groupIndex].longServicePay = amount > 0 
      ? new Intl.NumberFormat('ko-KR').format(amount) 
      : '';
  });
  
  store.commit('showSnackbar', { text: '장기근속수당이 자동 계산되었습니다.', color: 'success' });
};

// 장기근속수당 일괄 미지급
const handleBulkLongServiceClear = () => {
  groupedMemberData.value.forEach((group, groupIndex) => {
    settingsData.value[groupIndex].longServicePay = '';
  });
  
  store.commit('showSnackbar', { text: '장기근속수당이 일괄 미지급으로 설정되었습니다.', color: 'success' });
};

// 근로자의날 수당 적용 (5월만)
const handleBulkLaborDayAllowance = (value: boolean) => {
  // 현재는 DB에 lemi_apply_labor_day_pay 필드가 있지만 StepThree에서는 직접 관리하지 않음
  // 필요시 추가 구현
  const message = value ? '근로자의날 수당이 일괄 지급으로 설정되었습니다.' : '근로자의날 수당이 일괄 미지급으로 설정되었습니다.';
  store.commit('showSnackbar', { text: message, color: 'success' });
};

// WageEmployeeModal 열기 핸들러
const openEmployeeModal = (index: number) => {
  selectedEmployeeIndex.value = index;
  selectedEmployee.value = groupedMemberData.value[index];
  showEmployeeModal.value = true;
};

// WageEmployeeModal에서 설정 업데이트 핸들러
const handleUpdateSetting = (groupIndex: number, key: string, value: any) => {
  if (!settingsData.value[groupIndex]) return;
  (settingsData.value[groupIndex] as any)[key] = value;
  checkUnsavedChanges();
};

// WageEmployeeModal에서 시급 업데이트 핸들러
const handleUpdateTimepay = (groupIndex: number, patientCode: string, sugaKey: string, value: string) => {
  if (!settingsData.value[groupIndex]) return;
  if (!settingsData.value[groupIndex].timePays[patientCode]) {
    settingsData.value[groupIndex].timePays[patientCode] = {};
  }
  settingsData.value[groupIndex].timePays[patientCode][sugaKey] = value;
  checkUnsavedChanges();
};

// 수가시간 모달 열기 (WageEmployeeModal에서 호출)
const openSugaHoursModalFromModal = (groupIndex: number, patient: any) => {
  currentSugaHoursGroupIndex.value = groupIndex;
  const sugaKey = getSugaKey(patient);
  currentSugaHoursPatient.value = {
    patientCode: patient.patientCode,
    patientName: patient.patientName,
    sugaKey: sugaKey,
    sugaName: patient.sugaName,
    originalHours: patient.sugaHours || 0
  };
  selectedSugaHours.value = getEffectiveSugaHours(groupIndex, patient);
  showSugaHoursModal.value = true;
};

// 개별 요양보호사 설정 초기화
const resetSingleCaregiverSettings = (groupIndex: number) => {
  const settings = settingsData.value[groupIndex];
  const group = groupedMemberData.value[groupIndex];
  if (!settings || !group) return;
  
  // 모든 시급 초기화
  group.patients.forEach((patient) => {
    const sugaKey = getSugaKey(patient);
    if (settings.timePays[patient.patientCode]) {
      settings.timePays[patient.patientCode][sugaKey] = '';
    }
  });
  
  // 체크박스 및 입력값 초기화
  settings.applyNationalPension = false;
  settings.yeonPay = '';
  settings.applyLongServicePay = false;
  settings.longServicePay = '';
  settings.applyEtcAllowance = false;
  settings.etcAllowance = '';
  settings.applyBoninPay = false;
  settings.boninPay = '';
  settings.applyEmpEtc = false;
  settings.empEtc = '';
  settings.customSugaHours = {};
  settings.sugaHoursChanged = false;
  
  checkUnsavedChanges();
  store.commit('showSnackbar', { text: '설정이 초기화되었습니다.', color: 'info' });
};

// 과거 설정 불러오기 (개별)
const loadPreviousSettings = async (groupIndex: number) => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code || !props.yearMonth) return;
  
  const group = groupedMemberData.value[groupIndex];
  if (!group) return;
  
  try {
    const response = await axios.get('/api/calc/previous-member-info', {
      params: {
        sisulCode: user.m_sisul_code,
        lebYm: props.yearMonth,
        caregiverName: group.name,
        caregiverBirth: group.birth
      },
      withCredentials: true
    });
    
    if (response.data.success && response.data.data && response.data.data.length > 0) {
      const prevData = response.data.data[0];
      const settings = settingsData.value[groupIndex];
      if (settings) {
        // 시급 적용
        group.patients.forEach((patient) => {
          const sugaKey = getSugaKey(patient);
          const matchingPrev = response.data.data.find((item: any) => 
            item.leb_patient_code === patient.patientCode && 
            item.suga_code === patient.sugaCode
          );
          if (matchingPrev && matchingPrev.time_pay) {
            if (!settings.timePays[patient.patientCode]) {
              settings.timePays[patient.patientCode] = {};
            }
            const numericValue = parseFloat(String(matchingPrev.time_pay).replace(/[^\d]/g, '')) || 0;
            settings.timePays[patient.patientCode][sugaKey] = numericValue > 0 
              ? new Intl.NumberFormat('ko-KR').format(numericValue) 
              : '';
          }
        });
        
        // 기타 설정 적용
        if (prevData.lemi_yeon_pay) {
          const numericValue = parseFloat(String(prevData.lemi_yeon_pay).replace(/[^\d]/g, '')) || 0;
          if (numericValue > 0) {
            settings.applyNationalPension = true;
            settings.yeonPay = new Intl.NumberFormat('ko-KR').format(numericValue);
          }
        }
        if (prevData.lemi_long_service_pay) {
          const numericValue = parseFloat(String(prevData.lemi_long_service_pay).replace(/[^\d]/g, '')) || 0;
          if (numericValue > 0) {
            settings.applyLongServicePay = true;
            settings.longServicePay = new Intl.NumberFormat('ko-KR').format(numericValue);
          }
        }
        
        checkUnsavedChanges();
        store.commit('showSnackbar', { text: '과거 설정을 불러왔습니다.', color: 'success' });
      }
    } else {
      store.commit('showSnackbar', { text: '과거 데이터가 없습니다.', color: 'warning' });
    }
  } catch (error) {
    console.error('과거 설정 불러오기 오류:', error);
    store.commit('showSnackbar', { text: '과거 설정 불러오기 중 오류가 발생했습니다.', color: 'error' });
  }
};

// 입사일/퇴사일 변경 핸들러
const handleStartDateUpdated = async (groupIndex: number, newStartDate: string, newEndDate: string | null = null) => {
  // 데이터 다시 로드
  await fetchMemberInfo();
};

// 직원 정보 아이템 생성 (WageEmployeeModal용)
const getEmployeeInfoItems = (group: GroupedMember, groupIndex: number) => {
  const items: { icon: string; color: string; text: string }[] = [];
  const age = calculateAge(group.birth, props.yearMonth || '');
  const totalHours = getTotalSugaHours(group, groupIndex);
  const totalDays = getTotalServiceDays(group);
  
  items.push({ icon: 'mdi-cake-variant', color: '#6b7280', text: `만 ${age}세` });
  items.push({ icon: 'mdi-clock-outline', color: '#6b7280', text: `${formatSugaHours(totalHours)} 근무` });
  items.push({ icon: 'mdi-counter', color: '#6b7280', text: `${totalDays}회 서비스` });
  
  if (group.workStartDate) {
    const startDate = group.workStartDate.replace(/[^0-9]/g, '');
    if (startDate.length >= 8) {
      items.push({ icon: 'mdi-calendar-start', color: '#6b7280', text: `입사일: ${startDate.slice(0, 4)}-${startDate.slice(4, 6)}-${startDate.slice(6, 8)}` });
    }
  }
  
  if (group.isBoss) {
    items.push({ icon: 'mdi-account-star', color: '#10b981', text: '대표자' });
  }
  
  return items;
};

// 제한사항 아이템 생성 (WageEmployeeModal용)
const getRestrictionItems = (group: GroupedMember, groupIndex: number) => {
  const items: { icon: string; color: string; text: string }[] = [];
  const age = calculateAge(group.birth, props.yearMonth || '');
  const totalHours = getTotalSugaHours(group, groupIndex);
  const isFamilyOnly = isAllFamilyCaregiver(group);
  
  if (age >= 65) {
    items.push({ icon: 'mdi-alert', color: '#f59e0b', text: '65세 이상 - 고용보험 납부의무 없음' });
  }
  if (age >= 60) {
    items.push({ icon: 'mdi-alert', color: '#f59e0b', text: '60세 이상 - 국민연금 납부의무 없음' });
  }
  if (isFamilyOnly && totalHours < 60) {
    items.push({ icon: 'mdi-alert', color: '#f59e0b', text: '가족요양 60시간 미만 - 4대보험 가입불가' });
  }
  if (group.isBoss) {
    items.push({ icon: 'mdi-alert', color: '#f59e0b', text: '대표자 - 고용보험/퇴직금 적용불가' });
  }
  
  return items;
};

// 모든 설정 초기화
const handleResetAllSettings = () => {
  groupedMemberData.value.forEach((group, groupIndex) => {
    const settings = settingsData.value[groupIndex];
    if (!settings) return;
    
    // 모든 시급 초기화
    group.patients.forEach((patient) => {
      const sugaKey = getSugaKey(patient);
      if (settings.timePays[patient.patientCode]) {
        settings.timePays[patient.patientCode][sugaKey] = '';
      }
    });
    
    // 모든 체크박스 해제
    settings.applyNationalPension = false;
    settings.yeonPay = '';
    settings.applyHealthInsurance = false;
    settings.applyEmploymentInsurance = false;
    settings.applyUnemployment = false;
    settings.applyRetirement = false;
    settings.applyHoliday = false;
    settings.applyOvertime = false;
    settings.applyMeal = false;
    settings.applyLongServicePay = false;
    settings.longServicePay = '';
    settings.applyEtcAllowance = false;
    settings.etcAllowance = '';
    settings.applyBoninPay = false;
    settings.boninPay = '';
    settings.applyEmpEtc = false;
    settings.empEtc = '';
    
    // 수가시간 수동 조정 초기화
    settings.customSugaHours = {};
    settings.sugaHoursChanged = false;
  });
  
  checkUnsavedChanges();
  store.commit('showSnackbar', { text: '⚠️ 모든 급여 설정이 초기화되었습니다. 저장하지 않으면 적용되지 않습니다.', color: 'warning' });
};

// 모든 과거 설정 불러오기
const handleApplyAllPreviousSettings = (data: { allData: Array<{ lebYm: string; data: any[] }>; recentData: { lebYm: string; data: any[] } }) => {
  const { allData, recentData } = data;
  
  if ((!allData || allData.length === 0) && !recentData) {
    store.commit('showSnackbar', { text: '과거 데이터가 없습니다.', color: 'warning' });
    return;
  }
  
  let appliedCount = 0;
  let timePayAppliedCount = 0;
  
  // 현재 데이터의 각 요양보호사에 대해
  groupedMemberData.value.forEach((group, groupIndex) => {
    const caregiverName = group.name;
    const caregiverBirth = group.birth;
    let foundAnyTimePay = false;
    
    // 시간당 임금: 여러 달 데이터에서 찾기 (최신순)
    // 각 수급자별로 독립적으로 시급 찾기 (모든 월 데이터 순회)
    if (allData && allData.length > 0) {
      group.patients.forEach((patient) => {
        // 270분초과의 경우 수가시간별 키 사용
        const sugaKey = getSugaKey(patient);
        
        // 이미 시급이 설정되어 있으면 스킵
        const currentTimePay = settingsData.value[groupIndex]?.timePays?.[patient.patientCode]?.[sugaKey];
        if (currentTimePay && parseFloat(String(currentTimePay).replace(/[^\d]/g, '')) > 0) {
          return;
        }
        
        // 각 월 데이터에서 해당 수급자의 시급 찾기 (최신순)
        for (const monthData of allData) {
          const matchingPrevious = monthData.data.find((item: any) =>
            item.caregiver_name === caregiverName &&
            item.caregiver_birth === caregiverBirth &&
            item.leb_patient_code === patient.patientCode &&
            item.suga_code === patient.sugaCode &&
            item.time_pay
          );
          
          if (matchingPrevious && matchingPrevious.time_pay) {
            if (!settingsData.value[groupIndex].timePays[patient.patientCode]) {
              settingsData.value[groupIndex].timePays[patient.patientCode] = {};
            }
            const numericValue = parseFloat(String(matchingPrevious.time_pay).replace(/[^\d]/g, '')) || 0;
            if (numericValue > 0) {
              settingsData.value[groupIndex].timePays[patient.patientCode][sugaKey] = 
                new Intl.NumberFormat('ko-KR').format(numericValue);
              foundAnyTimePay = true;
              break; // 해당 수급자의 시급을 찾았으면 다음 수급자로
            }
          }
        }
        
        // 해당 수급자의 시급을 못 찾았으면, 같은 요양보호사의 다른 시급 사용 (fallback)
        if (!foundAnyTimePay) {
          for (const monthData of allData) {
            const anyPreviousTimePay = monthData.data.find((item: any) =>
              item.caregiver_name === caregiverName &&
              item.caregiver_birth === caregiverBirth &&
              item.time_pay
            );
            
            if (anyPreviousTimePay && anyPreviousTimePay.time_pay) {
              if (!settingsData.value[groupIndex].timePays[patient.patientCode]) {
                settingsData.value[groupIndex].timePays[patient.patientCode] = {};
              }
              const numericValue = parseFloat(String(anyPreviousTimePay.time_pay).replace(/[^\d]/g, '')) || 0;
              if (numericValue > 0) {
                settingsData.value[groupIndex].timePays[patient.patientCode][sugaKey] = 
                  new Intl.NumberFormat('ko-KR').format(numericValue);
                foundAnyTimePay = true;
                break;
              }
            }
          }
        }
      });
      
      if (foundAnyTimePay) {
        timePayAppliedCount++;
      }
    }
    
    // 수당 및 보험: 최근 1개월 데이터에서 적용
    if (recentData && recentData.data) {
      const recentCaregiver = recentData.data.find((item: any) =>
        item.caregiver_name === caregiverName && item.caregiver_birth === caregiverBirth
      );
      
      if (recentCaregiver) {
        // 국민연금 금액 포맷팅
        let yeonPay = recentCaregiver.lemi_yeon_pay || '';
        let applyNationalPension = false;
        if (yeonPay) {
          const numericValue = parseFloat(String(yeonPay).replace(/[^\d]/g, '')) || 0;
          if (numericValue > 0) {
            yeonPay = new Intl.NumberFormat('ko-KR').format(numericValue);
            applyNationalPension = true;
          }
        }
        settingsData.value[groupIndex].applyNationalPension = applyNationalPension;
        settingsData.value[groupIndex].yeonPay = yeonPay;
        
        // 건강보험, 고용보험, 퇴직금, 휴일수당, 연장수당, 식대
        settingsData.value[groupIndex].applyHealthInsurance = recentCaregiver.lemi_apply_gun === 'Y';
        settingsData.value[groupIndex].applyEmploymentInsurance = recentCaregiver.lemi_apply_go === 'Y';
        settingsData.value[groupIndex].applyRetirement = recentCaregiver.lemi_apply_out_pay === 'Y';
        settingsData.value[groupIndex].applyHoliday = recentCaregiver.lemi_apply_holiday_pay === 'Y';
        settingsData.value[groupIndex].applyOvertime = recentCaregiver.lemi_apply_overtime_pay === 'Y';
        settingsData.value[groupIndex].applyMeal = recentCaregiver.lemi_apply_lunch_pay === 'Y';
        
        // 실업급여 설정 (lemi_sil_pay에 값이 있으면 실업급여 적용)
        const silPay = recentCaregiver.lemi_sil_pay || '';
        const hasSilPay = silPay && parseFloat(String(silPay).replace(/[^\d]/g, '')) > 0;
        settingsData.value[groupIndex].applyUnemployment = 
          (recentCaregiver.lemi_apply_go === 'Y') || hasSilPay;
        
        // 장기근속수당 포맷팅 및 체크박스 설정
        let longServicePay = recentCaregiver.lemi_long_service_pay || '';
        let applyLongServicePay = false;
        if (longServicePay) {
          const numericValue = parseFloat(String(longServicePay).replace(/[^\d]/g, '')) || 0;
          if (numericValue > 0) {
            longServicePay = new Intl.NumberFormat('ko-KR').format(numericValue);
            applyLongServicePay = true;
          }
        }
        settingsData.value[groupIndex].applyLongServicePay = applyLongServicePay;
        settingsData.value[groupIndex].longServicePay = longServicePay;
        
        // 기타수당 포맷팅 및 체크박스 설정
        let etcAllowance = recentCaregiver.lemi_etc_allowance || '';
        let applyEtcAllowance = false;
        if (etcAllowance) {
          const numericValue = parseFloat(String(etcAllowance).replace(/[^\d]/g, '')) || 0;
          if (numericValue > 0) {
            etcAllowance = new Intl.NumberFormat('ko-KR').format(numericValue);
            applyEtcAllowance = true;
          }
        }
        settingsData.value[groupIndex].applyEtcAllowance = applyEtcAllowance;
        settingsData.value[groupIndex].etcAllowance = etcAllowance;
        
        // 본인부담금공제 포맷팅 및 체크박스 설정
        let boninPay = recentCaregiver.lemi_bonin_pay || '';
        let applyBoninPay = false;
        if (boninPay) {
          const numericValue = parseFloat(String(boninPay).replace(/[^\d]/g, '')) || 0;
          if (numericValue > 0) {
            boninPay = new Intl.NumberFormat('ko-KR').format(numericValue);
            applyBoninPay = true;
          }
        }
        settingsData.value[groupIndex].applyBoninPay = applyBoninPay;
        settingsData.value[groupIndex].boninPay = boninPay;
        
        // 기타공제 포맷팅 및 체크박스 설정
        let empEtc = recentCaregiver.lemi_emp_etc || '';
        let applyEmpEtc = false;
        if (empEtc) {
          const numericValue = parseFloat(String(empEtc).replace(/[^\d]/g, '')) || 0;
          if (numericValue > 0) {
            empEtc = new Intl.NumberFormat('ko-KR').format(numericValue);
            applyEmpEtc = true;
          }
        }
        settingsData.value[groupIndex].applyEmpEtc = applyEmpEtc;
        settingsData.value[groupIndex].empEtc = empEtc;
        
        appliedCount++;
      }
    }
  });
  
  // 변경사항 체크
  checkUnsavedChanges();
  
  // 결과 메시지
  let message = '';
  if (appliedCount > 0 && timePayAppliedCount > 0) {
    message = `${appliedCount}명의 과거 설정이 적용되었습니다. (시급: ${timePayAppliedCount}명)`;
  } else if (appliedCount > 0) {
    message = `${appliedCount}명의 과거 설정이 적용되었습니다.`;
  } else if (timePayAppliedCount > 0) {
    message = `${timePayAppliedCount}명의 시급이 적용되었습니다.`;
  } else {
    message = '적용할 과거 설정이 없습니다.';
  }
  
  store.commit('showSnackbar', { 
    text: message, 
    color: appliedCount > 0 || timePayAppliedCount > 0 ? 'success' : 'warning'
  });
};

// 연월 변경 시 데이터 다시 조회
watch(() => props.yearMonth, () => {
  if (props.yearMonth) {
    expandedTimePay.value.clear();
    settingsData.value = {};
    fetchMemberInfo();
  }
}, { immediate: true });

// 설정 변경 시 급여 재계산
watch(settingsData, () => {
  groupedMemberData.value.forEach((group, index) => {
    payrollDetails.value[index] = calculatePayrollDetails(group, index);
  });
}, { deep: true });

watch(groupedMemberData, () => {
  initializeSettingsData();
}, { deep: true });

onMounted(() => {
  if (props.yearMonth) {
    fetchMemberInfo();
  }
});
</script>

<style lang="scss">
@import '@/assets/styles/wageProcess.scss';
@import '@/assets/styles/StepThree.scss';
</style>
